import {
  BuildSummary,
  mergeBuildSummaryMap,
  patchBuildSummaryMap,
  removeBuildSummaryFromMap
} from '~/helpers/buildSummaryHelpers';
import {
  getBuildRunEventLogicalIdentity,
  normalizeBuildRunEventCreatedAt
} from './runEventIdentity';
import { createFallbackBuildRunMessageId } from './messageIdentity';

export interface BuildLiveRunMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  codeGenerated: string | null;
  streamCodePreview?: string | null;
  billingState?: 'charged' | 'not_charged' | 'pending' | null;
  artifactVersionId?: number | null;
  clientMessageId?: string | null;
  createdAt: number;
  persisted?: boolean;
}

export interface BuildLiveRunUsageMetric {
  stage: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface BuildLiveRunEvent {
  id: string;
  schemaVersion?: number | null;
  eventType?: string | null;
  source?: string | null;
  threadId?: string | null;
  requestId?: string | null;
  sequence?: number | null;
  buildId?: number | null;
  userId?: number | null;
  kind: 'lifecycle' | 'phase' | 'action' | 'status' | 'usage';
  phase: string | null;
  message: string;
  createdAt: number;
  deduped?: boolean;
  details?:
    | ({
        thoughtContent?: string | null;
        thoughtContentIsDelta?: boolean;
        isComplete?: boolean;
        isThinkingHard?: boolean;
      } & Record<string, any>)
    | null;
  usage?:
    | ({
        stage?: string | null;
        model?: string | null;
        inputTokens?: number;
        outputTokens?: number;
        totalTokens?: number;
      } & Record<string, any>)
    | null;
}

export interface BuildLiveRunFollowUpPrompt {
  question?: string | null;
  suggestedMessage?: string | null;
  sourceMessageId?: number | null;
  modelSwitch?: { model: string; mode: string; label: string } | null;
  mode?: string | null;
}

export interface BuildLiveRunDeferredRequest {
  message: string;
  messageContext?: string | null;
  planAction?:
    | 'continue'
    | 'cancel'
    | 'pivot'
    | 'start'
    | 'smaller'
    | 'skip'
    | null;
  stopActiveRun?: boolean | null;
  stopRequestId?: string | null;
}

export interface BuildLiveRunState {
  buildId: number;
  requestId: string;
  runMode: 'user' | 'greeting' | 'runtime-autofix';
  generating: boolean;
  terminalState?: 'complete' | 'error' | 'stopped' | null;
  stopReason?: 'user' | 'replacement' | null;
  interruptionReason?: 'tool_limit' | 'energy_depleted' | 'energy_budget' | null;
  error: string | null;
  status: string | null;
  assistantStatusSteps: string[];
  agentContext?: Record<string, any> | null;
  lifecycle?: Record<string, any> | null;
  usageMetrics: Record<string, BuildLiveRunUsageMetric>;
  runEvents: BuildLiveRunEvent[];
  userMessage: BuildLiveRunMessage | null;
  assistantMessage: BuildLiveRunMessage | null;
  baseProjectFiles: Array<{ path: string; content?: string }>;
  projectFilesHash?: string | null;
  streamingProjectFiles: Array<{ path: string; content?: string }> | null;
  streamingFocusFilePath: string | null;
  executionPlan?: any | null;
  followUpPrompt?: BuildLiveRunFollowUpPrompt | null;
  pendingToolApproval?: Record<string, any> | null;
  thumbnailNudge?: { sourceMessageId: number } | null;
  deferredBuildRequest?: BuildLiveRunDeferredRequest | null;
  runtimeExplorationPlan?: any | null;
  runtimePlanRefined?: boolean;
  billingState?: 'charged' | 'not_charged' | 'pending' | null;
  requestLimits?: any | null;
  updatedAt: number;
}

export interface BuildWorkspaceSnapshot {
  build: any;
  chatMessages: any[];
  copilotPolicy: any | null;
  updatedAt: number;
}

export type BuildStudioTab =
  'mine' | 'collaborating' | 'community' | 'open_source';
type BuildStudioBrowseTab = Exclude<BuildStudioTab, 'mine'>;
type BuildStudioPublicBrowseTab = Exclude<
  BuildStudioBrowseTab,
  'collaborating'
>;
export type BuildStudioBrowseMode = 'recent' | 'leaderboard';
export type BuildStudioSection = 'apps' | 'prompts';
export type PromptStudioTab = 'my' | 'community';
export type PromptStudioBrowseMode = 'recent' | 'leaderboard';
export type BuildActivityTab = 'all' | 'mine' | 'collaborating' | 'favorites';
export type BuildActivitySubtab = 'all' | 'notifications' | 'branch_updates';
export type BuildWorkspaceCommunicationMode = 'lumine' | 'versions' | 'people';

export interface BuildWorkspaceUiState {
  communicationMode: BuildWorkspaceCommunicationMode;
  forumCache: BuildWorkspaceForumCacheState;
  scrollTops: Partial<Record<BuildWorkspaceCommunicationMode, number>>;
  selectedForumThreadId: number;
}

export interface BuildWorkspaceForumScopeCacheState {
  threads: any[];
  loadedAt: number;
}

export interface BuildWorkspaceForumThreadCacheState {
  thread: any;
  replies: any[];
  loadedAt: number;
}

export interface BuildWorkspaceForumCacheState {
  viewerKey: string;
  scopes: Record<string, BuildWorkspaceForumScopeCacheState>;
  threadsById: Record<string, BuildWorkspaceForumThreadCacheState>;
}

export interface BuildStudioBrowseTabState {
  builds: any[];
  loadMoreToken: string | null;
  loaded: boolean;
  browseMode: BuildStudioBrowseMode;
  searchQuery: string;
  userId: number | null;
  cacheRefreshKey: number;
  cacheGeneration: number;
}

export interface PromptStudioListState {
  prompts: any[];
  loadMoreToken: string | null;
  loaded: boolean;
  browseMode: PromptStudioBrowseMode;
  searchQuery: string;
  userId: number | null;
  cacheGeneration: number;
}

export interface BuildStudioActivityPanelState {
  activeTab: BuildActivityTab;
  activeSubtab: BuildActivitySubtab;
}

export interface BuildStudioActivityFeedState {
  activities: any[];
  loadMoreToken: string | null;
  loaded: boolean;
  userId: number | null;
  loadedAt: number;
  lastViewedAllActivityAt: number;
  lastViewedAllActivitySourceRank: number;
  lastViewedAllActivitySortId: number;
}

export type BuildStudioQuickAccessMode = 'recent' | 'favorites';

export interface BuildStudioQuickAccessListState {
  builds: any[];
  cursor: string | null;
  loaded: boolean;
  userId: number | null;
}

export interface BuildStudioTodayTopViewedState {
  build: any | null;
  builds: any[];
  nextDay: number | null;
  loaded: boolean;
  userId: number | null;
}

export interface BuildStudioQuickAccessState {
  recent: BuildStudioQuickAccessListState;
  favorites: BuildStudioQuickAccessListState;
  todayTopViewed: BuildStudioTodayTopViewedState;
}

export interface BuildStudioState {
  section: BuildStudioSection;
  activeTab: BuildStudioTab;
  myBuilds: any[];
  myBuildsLoaded: boolean;
  myBuildsUserId: number | null;
  quickAccess: BuildStudioQuickAccessState;
  browse: Record<BuildStudioBrowseTab, BuildStudioBrowseTabState>;
  browseModes: Record<BuildStudioPublicBrowseTab, BuildStudioBrowseMode>;
  promptTab: PromptStudioTab;
  promptBrowseModes: {
    community: PromptStudioBrowseMode;
  };
  promptStudio: Record<PromptStudioTab, PromptStudioListState>;
  activityPanel: BuildStudioActivityPanelState;
  activityFeeds: Record<
    BuildActivityTab,
    Record<BuildActivitySubtab, BuildStudioActivityFeedState>
  >;
}

export interface BuildLiveRunStreamUpdatePayload {
  userMessageContent?: string | null;
  userClientMessageId?: string | null;
  status?: string | null;
  assistantStatusSteps?: string[];
  reply?: string;
  replyDelta?: string;
  codeGenerated?: string | null;
  codeGeneratedDelta?: string;
  userMessageId?: number | null;
  assistantMessageId?: number | null;
  assistantClientMessageId?: string | null;
  assistantMessageCreatedAt?: number | null;
  usageMetrics?: Record<string, BuildLiveRunUsageMetric>;
  baseProjectFiles?: Array<{ path: string; content?: string }> | null;
  projectFiles?: Array<{ path: string; content?: string }> | null;
  projectFilesMode?: 'patch' | 'snapshot' | null;
  projectFilesPersisted?: boolean;
  projectFilesFocusPath?: string | null;
  executionPlan?: any | null;
  followUpPrompt?: BuildLiveRunFollowUpPrompt | null;
  pendingToolApproval?: Record<string, any> | null;
  thumbnailNudge?: { sourceMessageId: number } | null;
  deferredBuildRequest?: BuildLiveRunDeferredRequest | null;
  runtimeExplorationPlan?: any | null;
  runtimePlanRefined?: boolean;
  billingState?: 'charged' | 'not_charged' | 'pending' | null;
  requestLimits?: any | null;
}

export interface BuildLiveRunRunningSnapshotPayload {
  status?: string | null;
  assistantStatusSteps?: string[];
  agentContext?: Record<string, any> | null;
  lifecycle?: Record<string, any> | null;
  usageMetrics?: Record<string, BuildLiveRunUsageMetric>;
  updatedAt?: number | null;
}

export interface BuildLiveRunActionPayload extends BuildLiveRunStreamUpdatePayload {
  buildId?: number;
  build?: any;
  chatMessages?: any[];
  copilotPolicy?: any | null;
  requestId?: string;
  runMode?: BuildLiveRunState['runMode'];
  generating?: boolean;
  status?: string | null;
  assistantStatusSteps?: string[];
  agentContext?: Record<string, any> | null;
  lifecycle?: Record<string, any> | null;
  userMessage?: BuildLiveRunMessage | null;
  assistantMessage?: BuildLiveRunMessage | null;
  baseProjectFiles?: Array<{ path: string; content?: string }> | null;
  runningSnapshot?: BuildLiveRunRunningSnapshotPayload | null;
  workspaceChanged?: boolean;
  event?: BuildLiveRunEvent | null;
  messageId?: number | null;
  messageRole?: BuildLiveRunMessage['role'] | null;
  clientMessageId?: string | null;
  assistantText?: string;
  stopReason?: 'user' | 'replacement' | null;
  artifactCode?: string | null;
  projectFilesHash?: string | null;
  artifactVersionId?: number | null;
  persistedAssistantId?: number | null;
  persistedUserId?: number | null;
  createdAt?: number;
  interruptionReason?: 'tool_limit' | 'energy_depleted' | 'energy_budget' | null;
  error?: string;
  preserveAssistantArtifactsOnError?: boolean;
  preserveTransientUserMessage?: boolean;
  preserveTransientAssistantMessage?: boolean;
  updatedAt?: number;
}

export interface BuildRuntimeVerifyResultPayload {
  buildId?: number | null;
  requestId?: string;
  status?: 'complete' | 'error' | null;
  improved?: boolean;
  reason?: string | null;
  shouldRepairAgain?: boolean;
  nextRemainingRepairs?: number | null;
  error?: string | null;
}

export interface BuildRuntimeVerifyResult {
  buildId: number | null;
  requestId: string;
  status: 'complete' | 'error';
  improved: boolean;
  reason: string | null;
  shouldRepairAgain: boolean;
  nextRemainingRepairs: number;
  error: string | null;
}

export interface BuildStudioActionPayload {
  nextDay?: number | null;
  section?: BuildStudioSection | string | null;
  activeTab?: BuildStudioTab;
  tab?: BuildStudioTab;
  promptTab?: PromptStudioTab | string | null;
  promptBrowseMode?: PromptStudioBrowseMode | string | null;
  prompts?: any[];
  prompt?: any;
  builds?: any[];
  build?: any;
  buildId?: number;
  buildIds?: number[];
  userId?: number | null;
  loadMoreToken?: string | null;
  cursor?: string | null;
  browseMode?: BuildStudioBrowseMode | string | null;
  quickAccessMode?: BuildStudioQuickAccessMode | string | null;
  searchQuery?: string | null;
  isFavorited?: boolean | null;
  favoritedAt?: number | null;
  favoriteActivityAt?: number | null;
  cacheRefreshKey?: number | null;
  cacheGeneration?: number | null;
  activityTab?: BuildActivityTab | string | null;
  activitySubtab?: BuildActivitySubtab | string | null;
  activities?: any[];
  activityLoadedAt?: number | null;
  lastViewedAllActivityAt?: number | null;
  lastViewedAllActivitySourceRank?: number | null;
  lastViewedAllActivitySortId?: number | null;
}

export interface BuildWorkspaceUiActionPayload {
  buildId?: number | null;
  communicationMode?: BuildWorkspaceCommunicationMode | string | null;
  scrollMode?: BuildWorkspaceCommunicationMode | string | null;
  scrollTop?: number | null;
  forumThreadId?: number | null;
}

export interface BuildWorkspaceForumCacheActionPayload {
  buildId?: number | null;
  viewerKey?: string | null;
  scopeKey?: string | null;
  threads?: any[];
  thread?: any | null;
  threadId?: number | null;
  replies?: any[] | null;
}

export interface BuildForumInvalidationActionPayload {
  rootBuildId?: number | null;
}

export interface BuildState {
  buildsById: Record<string, BuildSummary>;
  buildRuns: Record<string, BuildLiveRunState>;
  buildRunRequestMap: Record<string, number>;
  buildWorkspaces: Record<string, BuildWorkspaceSnapshot>;
  buildWorkspaceUi: Record<string, BuildWorkspaceUiState>;
  forumInvalidationVersions: Record<string, number>;
  runtimeVerifyResults: Record<string, BuildRuntimeVerifyResult>;
  buildStudio: BuildStudioState;
}

export interface BuildAction {
  type:
    | 'REGISTER_BUILD_RUN'
    | 'UPDATE_BUILD_RUN_STATUS'
    | 'UPDATE_BUILD_RUN_STREAM'
    | 'APPLY_BUILD_RUN_RUNNING_SNAPSHOT'
    | 'APPEND_BUILD_RUN_EVENT'
    | 'COMPLETE_BUILD_RUN'
    | 'FAIL_BUILD_RUN'
    | 'STOP_BUILD_RUN'
    | 'REMOVE_BUILD_RUN_MESSAGE'
    | 'SET_BUILD_WORKSPACE'
    | 'REMOVE_BUILD_WORKSPACE'
    | 'SET_BUILD_WORKSPACE_COMMUNICATION_MODE'
    | 'SET_BUILD_WORKSPACE_SCROLL'
    | 'SET_BUILD_WORKSPACE_FORUM_THREAD'
    | 'SET_BUILD_WORKSPACE_FORUM_THREADS'
    | 'SET_BUILD_WORKSPACE_FORUM_THREAD_DETAIL'
    | 'REMOVE_BUILD_WORKSPACE_FORUM_THREAD'
    | 'CLEAR_BUILD_WORKSPACE_FORUM_CACHE'
    | 'INVALIDATE_BUILD_FORUM'
    | 'SET_BUILD_STUDIO_ACTIVE_TAB'
    | 'SET_BUILD_STUDIO_SECTION'
    | 'SET_BUILD_STUDIO_MY_BUILDS'
    | 'PATCH_BUILD_STUDIO_MY_BUILD'
    | 'REMOVE_BUILD_STUDIO_MY_BUILD'
    | 'SET_BUILD_STUDIO_TODAY_TOP_VIEWED_BUILD'
    | 'SET_BUILD_STUDIO_QUICK_ACCESS_BUILDS'
    | 'APPEND_BUILD_STUDIO_QUICK_ACCESS_BUILDS'
    | 'REMOVE_BUILD_STUDIO_QUICK_ACCESS_BUILDS'
    | 'PATCH_BUILD_STUDIO_QUICK_ACCESS_FAVORITE'
    | 'SET_BUILD_STUDIO_BROWSE_MODE'
    | 'SET_BUILD_STUDIO_ACTIVITY_FILTER'
    | 'INVALIDATE_BUILD_STUDIO_ACTIVITY_FEEDS'
    | 'INVALIDATE_BUILD_STUDIO_BROWSE_TAB'
    | 'SET_BUILD_STUDIO_ACTIVITY_ITEMS'
    | 'APPEND_BUILD_STUDIO_ACTIVITY_ITEMS'
    | 'SET_BUILD_STUDIO_ACTIVITY_VIEWED'
    | 'SET_BUILD_STUDIO_BROWSE_BUILDS'
    | 'APPEND_BUILD_STUDIO_BROWSE_BUILDS'
    | 'SET_PROMPT_STUDIO_TAB'
    | 'SET_PROMPT_STUDIO_BROWSE_MODE'
    | 'INVALIDATE_PROMPT_STUDIO_TAB'
    | 'SET_PROMPT_STUDIO_ITEMS'
    | 'APPEND_PROMPT_STUDIO_ITEMS'
    | 'PATCH_PROMPT_STUDIO_CLONE'
    | 'UPSERT_BUILD_SUMMARIES'
    | 'PATCH_BUILD_SUMMARY'
    | 'REMOVE_BUILD_SUMMARY'
    | 'PUBLISH_BUILD_RUNTIME_VERIFY_RESULT'
    | 'CLEAR_BUILD_RUNTIME_VERIFY_RESULT'
    | 'CLEAR_BUILD_RUN'
    | 'RESET_BUILD_RUNS';
  buildId?: number;
  buildRun?: BuildLiveRunActionPayload;
  runtimeVerifyResult?: BuildRuntimeVerifyResultPayload;
  buildStudio?: BuildStudioActionPayload;
  buildWorkspaceUi?: BuildWorkspaceUiActionPayload;
  buildWorkspaceForumCache?: BuildWorkspaceForumCacheActionPayload;
  buildForumInvalidation?: BuildForumInvalidationActionPayload;
  buildSummary?: {
    build?: any;
    builds?: any[];
    buildId?: number;
    patch?: Record<string, unknown>;
  };
}

export function createInitialBuildStudioState(): BuildStudioState {
  return {
    section: 'apps',
    activeTab: 'mine',
    myBuilds: [],
    myBuildsLoaded: false,
    myBuildsUserId: null,
    quickAccess: createInitialBuildStudioQuickAccessState(),
    browse: {
      community: createInitialBuildStudioBrowseState(),
      collaborating: createInitialBuildStudioBrowseState(),
      open_source: createInitialBuildStudioBrowseState()
    },
    browseModes: createInitialBuildStudioBrowseModes(),
    promptTab: 'community',
    promptBrowseModes: {
      community: 'recent'
    },
    promptStudio: {
      my: createInitialPromptStudioListState(),
      community: createInitialPromptStudioListState()
    },
    activityPanel: createInitialBuildStudioActivityPanelState(),
    activityFeeds: createInitialBuildStudioActivityFeeds()
  };
}

function createInitialBuildStudioBrowseModes(): Record<
  BuildStudioPublicBrowseTab,
  BuildStudioBrowseMode
> {
  return {
    community: 'recent',
    open_source: 'recent'
  };
}

function createInitialBuildStudioActivityPanelState(): BuildStudioActivityPanelState {
  return {
    activeTab: 'all',
    activeSubtab: 'all'
  };
}

function createInitialBuildStudioActivityFeeds(): Record<
  BuildActivityTab,
  Record<BuildActivitySubtab, BuildStudioActivityFeedState>
> {
  return {
    all: {
      all: createInitialBuildStudioActivityFeedState(),
      notifications: createInitialBuildStudioActivityFeedState(),
      branch_updates: createInitialBuildStudioActivityFeedState()
    },
    mine: {
      all: createInitialBuildStudioActivityFeedState(),
      notifications: createInitialBuildStudioActivityFeedState(),
      branch_updates: createInitialBuildStudioActivityFeedState()
    },
    collaborating: {
      all: createInitialBuildStudioActivityFeedState(),
      notifications: createInitialBuildStudioActivityFeedState(),
      branch_updates: createInitialBuildStudioActivityFeedState()
    },
    favorites: {
      all: createInitialBuildStudioActivityFeedState(),
      notifications: createInitialBuildStudioActivityFeedState(),
      branch_updates: createInitialBuildStudioActivityFeedState()
    }
  };
}

function createInitialBuildStudioActivityFeedState(): BuildStudioActivityFeedState {
  return {
    activities: [],
    loadMoreToken: null,
    loaded: false,
    userId: null,
    loadedAt: 0,
    lastViewedAllActivityAt: 0,
    lastViewedAllActivitySourceRank: 0,
    lastViewedAllActivitySortId: 0
  };
}

function createInitialBuildStudioBrowseState(): BuildStudioBrowseTabState {
  return {
    builds: [],
    loadMoreToken: null,
    loaded: false,
    browseMode: 'recent',
    searchQuery: '',
    userId: null,
    cacheRefreshKey: 0,
    cacheGeneration: 0
  };
}

export function createInitialPromptStudioListState(): PromptStudioListState {
  return {
    prompts: [],
    loadMoreToken: null,
    loaded: false,
    browseMode: 'recent',
    searchQuery: '',
    userId: null,
    cacheGeneration: 0
  };
}

function createInitialBuildStudioQuickAccessState(): BuildStudioQuickAccessState {
  return {
    recent: createInitialBuildStudioQuickAccessListState(),
    favorites: createInitialBuildStudioQuickAccessListState(),
    todayTopViewed: createInitialBuildStudioTodayTopViewedState()
  };
}

function createInitialBuildStudioQuickAccessListState(): BuildStudioQuickAccessListState {
  return {
    builds: [],
    cursor: null,
    loaded: false,
    userId: null
  };
}

function createInitialBuildStudioTodayTopViewedState(): BuildStudioTodayTopViewedState {
  return {
    build: null,
    builds: [],
    nextDay: null,
    loaded: false,
    userId: null
  };
}

function getBuildRunLookupBuildId(
  state: BuildState,
  buildRun?: BuildLiveRunActionPayload
) {
  const explicitBuildId = Number(buildRun?.buildId || 0);
  if (explicitBuildId > 0) return explicitBuildId;
  const requestId = String(buildRun?.requestId || '').trim();
  if (!requestId) return 0;
  return Number(state.buildRunRequestMap[requestId] || 0) || 0;
}

function getBuildRunKey(buildId: number) {
  return String(Number(buildId) || 0);
}

function getBuildWorkspaceUiKey(buildId: number) {
  return String(Number(buildId) || 0);
}

function createInitialBuildWorkspaceUiState(): BuildWorkspaceUiState {
  return {
    communicationMode: 'lumine',
    forumCache: createInitialBuildWorkspaceForumCacheState(),
    scrollTops: {},
    selectedForumThreadId: 0
  };
}

function createInitialBuildWorkspaceForumCacheState(
  viewerKey = ''
): BuildWorkspaceForumCacheState {
  return {
    viewerKey: normalizeBuildWorkspaceForumViewerKey(viewerKey),
    scopes: {},
    threadsById: {}
  };
}

function getBuildWorkspaceUiState(
  state: BuildState,
  buildId: number
): BuildWorkspaceUiState {
  const key = getBuildWorkspaceUiKey(buildId);
  const currentUi =
    state.buildWorkspaceUi?.[key] || createInitialBuildWorkspaceUiState();
  return {
    ...currentUi,
    forumCache:
      currentUi.forumCache || createInitialBuildWorkspaceForumCacheState()
  };
}

function normalizeBuildWorkspaceCommunicationMode(
  value?: string | null
): BuildWorkspaceCommunicationMode {
  return value === 'people' || value === 'versions' ? value : 'lumine';
}

function getBuildRuntimeVerifyResultKey(
  runtimeVerifyResult?: BuildRuntimeVerifyResultPayload
) {
  const requestId = String(runtimeVerifyResult?.requestId || '').trim();
  if (!requestId) return '';
  const buildId = Number(runtimeVerifyResult?.buildId || 0);
  return `${buildId > 0 ? buildId : 0}:${requestId}`;
}

function getBuildStudioState(state: BuildState) {
  return state.buildStudio || createInitialBuildStudioState();
}

function getBuildStudioQuickAccessState(
  buildStudio: BuildStudioState
): BuildStudioQuickAccessState {
  return {
    ...createInitialBuildStudioQuickAccessState(),
    ...(buildStudio.quickAccess || {})
  };
}

function normalizeBuildStudioTab(value?: string | null): BuildStudioTab {
  return value === 'collaborating' ||
    value === 'community' ||
    value === 'open_source'
    ? value
    : 'mine';
}

function normalizeBuildStudioSection(
  value?: string | null
): BuildStudioSection {
  return value === 'prompts' ? 'prompts' : 'apps';
}

function normalizePromptStudioTab(value?: string | null): PromptStudioTab {
  return value === 'my' ? 'my' : 'community';
}

function normalizePromptStudioBrowseMode(
  value?: string | null
): PromptStudioBrowseMode {
  return value === 'leaderboard' ? 'leaderboard' : 'recent';
}

function normalizeBuildStudioBrowseTab(
  value?: string | null
): BuildStudioBrowseTab {
  if (value === 'collaborating') return 'collaborating';
  return value === 'open_source' ? 'open_source' : 'community';
}

function normalizeBuildStudioPublicBrowseTab(
  value?: string | null
): BuildStudioPublicBrowseTab {
  return value === 'open_source' ? 'open_source' : 'community';
}

function normalizeBuildStudioBrowseMode(
  value?: string | null
): BuildStudioBrowseMode {
  return value === 'leaderboard' ? 'leaderboard' : 'recent';
}

function normalizeBuildStudioQuickAccessMode(
  value?: string | null
): BuildStudioQuickAccessMode {
  return value === 'favorites' ? 'favorites' : 'recent';
}

function normalizeBuildStudioQuickAccessCursor(value: unknown) {
  const cursor = String(value || '').trim();
  return cursor || null;
}

function normalizeBuildStudioSearchQuery(value?: string | null) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
}

function normalizeBuildActivityTab(value?: string | null): BuildActivityTab {
  if (value === 'all') return 'all';
  if (value === 'mine' || value === 'collaborating' || value === 'favorites') {
    return value;
  }
  return 'all';
}

function normalizeBuildActivitySubtab(
  value?: string | null
): BuildActivitySubtab {
  if (value === 'all') return 'all';
  return value === 'branch_updates' ? 'branch_updates' : 'notifications';
}

function normalizeBuildStudioUserId(value: unknown) {
  const userId = Math.floor(Number(value) || 0);
  return userId > 0 ? userId : null;
}

function normalizeBuildStudioNullablePositiveInteger(value: unknown) {
  const normalized = Math.floor(Number(value) || 0);
  if (!Number.isFinite(normalized)) return null;
  return normalized > 0 ? normalized : null;
}

function normalizeBuildStudioCacheRefreshKey(value: unknown) {
  const refreshKey = Math.floor(Number(value) || 0);
  if (!Number.isFinite(refreshKey)) return 0;
  return Math.max(0, refreshKey);
}

function normalizeBuildStudioCacheGeneration(value: unknown) {
  const generation = Math.floor(Number(value) || 0);
  if (!Number.isFinite(generation)) return 0;
  return Math.max(0, generation);
}

function normalizeBuildStudioActivityLoadedAt(value: unknown) {
  const loadedAt = Math.floor(Number(value) || 0);
  return loadedAt > 0 ? loadedAt : Date.now();
}

function normalizeBuildStudioActivityViewedAt(value: unknown) {
  const viewedAt = Math.floor(Number(value) || 0);
  if (!Number.isFinite(viewedAt)) return 0;
  return Math.max(0, viewedAt);
}

interface BuildStudioActivityViewedPosition {
  timeStamp: number;
  sourceRank: number;
  sortId: number;
}

function normalizeBuildStudioActivityPositiveInteger(value: unknown) {
  const normalized = Math.floor(Number(value) || 0);
  if (!Number.isFinite(normalized)) return 0;
  return Math.max(0, normalized);
}

function normalizeBuildStudioActivityViewedPosition({
  sourceRank,
  sortId,
  timeStamp
}: {
  sourceRank?: unknown;
  sortId?: unknown;
  timeStamp?: unknown;
}): BuildStudioActivityViewedPosition {
  return {
    timeStamp: normalizeBuildStudioActivityViewedAt(timeStamp),
    sourceRank: normalizeBuildStudioActivityPositiveInteger(sourceRank),
    sortId: normalizeBuildStudioActivityPositiveInteger(sortId)
  };
}

function getBuildStudioActivityViewedPositionFromPayload(
  payload?: BuildStudioActionPayload | null
) {
  return normalizeBuildStudioActivityViewedPosition({
    timeStamp: payload?.lastViewedAllActivityAt,
    sourceRank: payload?.lastViewedAllActivitySourceRank,
    sortId: payload?.lastViewedAllActivitySortId
  });
}

function getBuildStudioActivityViewedPositionFromFeed(
  feed?: BuildStudioActivityFeedState | null
) {
  return normalizeBuildStudioActivityViewedPosition({
    timeStamp: feed?.lastViewedAllActivityAt,
    sourceRank: feed?.lastViewedAllActivitySourceRank,
    sortId: feed?.lastViewedAllActivitySortId
  });
}

function serializeBuildStudioActivityViewedPosition(
  position: BuildStudioActivityViewedPosition
) {
  return {
    lastViewedAllActivityAt: position.timeStamp,
    lastViewedAllActivitySourceRank: position.sourceRank,
    lastViewedAllActivitySortId: position.sortId
  };
}

function compareBuildStudioActivityViewedPositions(
  a: BuildStudioActivityViewedPosition,
  b: BuildStudioActivityViewedPosition
) {
  return (
    a.timeStamp - b.timeStamp ||
    a.sourceRank - b.sourceRank ||
    a.sortId - b.sortId
  );
}

function isValidBuildStudioActivityViewedPosition(
  position: BuildStudioActivityViewedPosition
) {
  return (
    position.timeStamp > 0 && position.sourceRank > 0 && position.sortId > 0
  );
}

function getBuildStudioActivityFeeds(buildStudio: BuildStudioState) {
  return {
    ...createInitialBuildStudioActivityFeeds(),
    ...(buildStudio.activityFeeds || {})
  };
}

function mergeBuildStudioQuickAccessItems(
  currentItems: any[],
  nextItems: any[]
) {
  const seenIds = new Set(currentItems.map((item) => Number(item?.id || 0)));
  const mergedItems = [...currentItems];
  for (const item of nextItems || []) {
    const id = Number(item?.id || 0);
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    mergedItems.push(item);
  }
  return mergedItems;
}

function removeBuildsFromQuickAccessList(
  listState: BuildStudioQuickAccessListState,
  buildIds: Set<number>,
  userId: number | null
) {
  if (userId && listState.userId && listState.userId !== userId) {
    return listState;
  }
  const nextBuilds = listState.builds.filter(
    (build) => !buildIds.has(Number(build?.id || 0))
  );
  return nextBuilds.length === listState.builds.length
    ? listState
    : {
        ...listState,
        builds: nextBuilds
      };
}

function patchBuildsForQuickAccessFavorite({
  buildId,
  builds,
  favoriteActivityAt,
  favoritedAt,
  isFavorited
}: {
  buildId: number;
  builds: any[];
  favoriteActivityAt: number | null;
  favoritedAt: number | null;
  isFavorited: boolean;
}) {
  return builds.map((build) =>
    Number(build?.id || 0) === buildId
      ? {
          ...build,
          favoriteActivityAt,
          favoritedAt,
          isFavorited
        }
      : build
  );
}

function patchQuickAccessListFavorite({
  build,
  buildId,
  favoriteActivityAt,
  favoritedAt,
  isFavorited,
  listState,
  mode,
  userId
}: {
  build: any;
  buildId: number;
  favoriteActivityAt: number | null;
  favoritedAt: number | null;
  isFavorited: boolean;
  listState: BuildStudioQuickAccessListState;
  mode: BuildStudioQuickAccessMode;
  userId: number | null;
}) {
  if (userId && listState.userId && listState.userId !== userId) {
    return listState;
  }
  if (!listState.loaded || listState.userId !== userId) {
    return listState;
  }
  if (mode === 'favorites') {
    if (!isFavorited) {
      return {
        ...listState,
        builds: listState.builds.filter(
          (item) => Number(item?.id || 0) !== buildId
        )
      };
    }
    const nextBuild = {
      ...build,
      id: buildId,
      favoriteActivityAt,
      favoritedAt,
      isFavorited: true
    };
    return {
      ...listState,
      builds: [
        nextBuild,
        ...patchBuildsForQuickAccessFavorite({
          buildId,
          builds: listState.builds.filter(
            (item) => Number(item?.id || 0) !== buildId
          ),
          favoriteActivityAt,
          favoritedAt,
          isFavorited
        })
      ]
    };
  }
  return {
    ...listState,
    builds: patchBuildsForQuickAccessFavorite({
      buildId,
      builds: listState.builds,
      favoriteActivityAt,
      favoritedAt,
      isFavorited
    })
  };
}

function mergeBuildStudioActivityItems(currentItems: any[], nextItems: any[]) {
  const seenIds = new Set(currentItems.map((item) => String(item?.id || '')));
  const mergedItems = [...currentItems];
  for (const item of nextItems || []) {
    const id = String(item?.id || '');
    if (id && seenIds.has(id)) continue;
    if (id) seenIds.add(id);
    mergedItems.push(item);
  }
  return mergedItems;
}

function mergePromptStudioItems(currentItems: any[], nextItems: any[]) {
  const seenIds = new Set(
    currentItems.map((item) => Number(item?.id || item?.subjectId || 0))
  );
  const mergedItems = [...currentItems];
  for (const item of nextItems || []) {
    const id = Number(item?.id || item?.subjectId || 0);
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    mergedItems.push(item);
  }
  return mergedItems;
}

function normalizeBuildWorkspaceScrollTop(value: unknown) {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized)) return 0;
  return Math.max(0, Math.floor(normalized));
}

function normalizeBuildWorkspaceForumThreadId(value: unknown) {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized)) return 0;
  return Math.max(0, Math.floor(normalized));
}

function normalizeBuildWorkspaceForumScopeKey(value: unknown) {
  const key = String(value || '').trim();
  return key || 'default';
}

function normalizeBuildWorkspaceForumViewerKey(value: unknown) {
  const key = String(value || '').trim();
  return key || 'viewer:unknown';
}

function getBuildWorkspaceForumThreadCacheKey(threadId: number) {
  return String(normalizeBuildWorkspaceForumThreadId(threadId));
}

function getBuildWorkspaceForumCacheForViewer(
  currentUi: BuildWorkspaceUiState,
  viewerKeyValue: unknown
) {
  const viewerKey = normalizeBuildWorkspaceForumViewerKey(viewerKeyValue);
  const currentForumCache =
    currentUi.forumCache || createInitialBuildWorkspaceForumCacheState();
  return currentForumCache.viewerKey === viewerKey
    ? currentForumCache
    : createInitialBuildWorkspaceForumCacheState(viewerKey);
}

function patchBuildWorkspaceForumThreadInScopes(
  scopes: Record<string, BuildWorkspaceForumScopeCacheState>,
  nextThread: any
) {
  const threadId = Number(nextThread?.id || 0);
  if (!threadId) return scopes;
  let changed = false;
  const nextScopes: Record<string, BuildWorkspaceForumScopeCacheState> = {};
  for (const [scopeKey, scope] of Object.entries(scopes || {})) {
    let scopeChanged = false;
    const nextThreads = (scope.threads || []).map((thread) => {
      if (Number(thread?.id || 0) !== threadId) return thread;
      scopeChanged = true;
      return nextThread;
    });
    changed = changed || scopeChanged;
    nextScopes[scopeKey] = scopeChanged
      ? {
          ...scope,
          threads: nextThreads,
          loadedAt: Date.now()
        }
      : scope;
  }
  return changed ? nextScopes : scopes;
}

function removeBuildWorkspaceForumThreadFromScopes(
  scopes: Record<string, BuildWorkspaceForumScopeCacheState>,
  threadId: number
) {
  const normalizedThreadId = normalizeBuildWorkspaceForumThreadId(threadId);
  if (!normalizedThreadId) return scopes;
  let changed = false;
  const nextScopes: Record<string, BuildWorkspaceForumScopeCacheState> = {};
  for (const [scopeKey, scope] of Object.entries(scopes || {})) {
    const nextThreads = (scope.threads || []).filter(
      (thread) => Number(thread?.id || 0) !== normalizedThreadId
    );
    const scopeChanged = nextThreads.length !== (scope.threads || []).length;
    changed = changed || scopeChanged;
    nextScopes[scopeKey] = scopeChanged
      ? {
          ...scope,
          threads: nextThreads,
          loadedAt: Date.now()
        }
      : scope;
  }
  return changed ? nextScopes : scopes;
}

function patchBuildStudioMyBuild(builds: any[], nextBuild?: any | null) {
  const buildId = Number(nextBuild?.id || 0);
  if (!buildId) return builds;
  return builds.map((build) =>
    Number(build?.id || 0) === buildId ? { ...build, ...nextBuild } : build
  );
}

function normalizeBuildRunProjectFilePath(rawPath: string) {
  const trimmedPath = String(rawPath || '')
    .trim()
    .replace(/\\/g, '/');
  if (!trimmedPath) return null;
  const normalized = trimmedPath.startsWith('/')
    ? trimmedPath
    : `/${trimmedPath}`;
  return normalized.replace(/\/{2,}/g, '/');
}

function normalizeBuildRunClientMessageId(value: unknown) {
  const normalized = String(value || '').trim();
  return normalized || null;
}

function getStreamingFocusFilePath(
  projectFiles?: Array<{ path: string; content?: string }> | null
) {
  const firstNonIndexPath = (projectFiles || [])
    .map((file) => {
      return normalizeBuildRunProjectFilePath(String(file?.path || ''));
    })
    .find((path): path is string => {
      if (!path) return false;
      const lowerPath = path.toLowerCase();
      return lowerPath !== '/index.html' && lowerPath !== '/index.htm';
    });
  return firstNonIndexPath || null;
}

function normalizeBuildRunProjectFiles(
  projectFiles?: Array<{ path: string; content?: string }> | null
) {
  const merged = new Map<string, string>();
  for (const file of projectFiles || []) {
    if (!file || typeof file !== 'object') continue;
    const rawPath = String(file.path || '')
      .trim()
      .replace(/\\/g, '/');
    if (!rawPath) continue;
    const normalized = (
      rawPath.startsWith('/') ? rawPath : `/${rawPath}`
    ).replace(/\/{2,}/g, '/');
    merged.set(
      normalized,
      typeof file.content === 'string' ? file.content : ''
    );
  }
  return Array.from(merged.entries()).map(([path, content]) => ({
    path,
    content
  }));
}

function overlayBuildRunProjectFiles({
  baseFiles,
  updates
}: {
  baseFiles: Array<{ path: string; content?: string }>;
  updates?: Array<{ path: string; content?: string }> | null;
}) {
  const merged = new Map<string, string>();
  for (const file of normalizeBuildRunProjectFiles(baseFiles)) {
    merged.set(file.path, typeof file.content === 'string' ? file.content : '');
  }
  for (const file of normalizeBuildRunProjectFiles(updates)) {
    merged.set(file.path, typeof file.content === 'string' ? file.content : '');
  }
  return Array.from(merged.entries()).map(([path, content]) => ({
    path,
    content
  }));
}

function appendAssistantStatusStep(
  steps: string[],
  status: string | null | undefined
) {
  const nextStatus = String(status || '').trim();
  if (!nextStatus) return steps;
  return steps[steps.length - 1] === nextStatus
    ? steps
    : [...steps, nextStatus];
}

function normalizeBuildRunAssistantStatusSteps(
  assistantStatusSteps?: string[],
  fallbackStatus?: string | null
) {
  const normalizedAssistantStatusSteps = Array.isArray(assistantStatusSteps)
    ? assistantStatusSteps.filter(
        (step): step is string =>
          typeof step === 'string' && step.trim().length > 0
      )
    : [];

  if (normalizedAssistantStatusSteps.length > 0) {
    return normalizedAssistantStatusSteps;
  }

  const normalizedFallbackStatus = String(fallbackStatus || '').trim();
  return normalizedFallbackStatus ? [normalizedFallbackStatus] : [];
}

function normalizeBuildRunUsageMetrics(
  usageMetrics?: Record<string, BuildLiveRunUsageMetric>
) {
  return Object.values(usageMetrics || {}).reduce<
    Record<string, BuildLiveRunUsageMetric>
  >((result, metric) => {
    const stage = String(metric?.stage || '').trim();
    const model = String(metric?.model || '').trim();
    if (!stage || !model) return result;
    result[stage] = {
      stage,
      model,
      inputTokens: Number(metric?.inputTokens || 0),
      outputTokens: Number(metric?.outputTokens || 0),
      totalTokens: Number(metric?.totalTokens || 0)
    };
    return result;
  }, {});
}

function normalizeBuildRunRecord(value?: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  try {
    const clonedValue = JSON.parse(JSON.stringify(value));
    return clonedValue &&
      typeof clonedValue === 'object' &&
      !Array.isArray(clonedValue)
      ? (clonedValue as Record<string, any>)
      : null;
  } catch {
    return null;
  }
}

function hasBuildRunEventStringValue(value?: string | null) {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasBuildRunEventNumberValue(value?: number | null) {
  const normalizedValue = Number(value || 0);
  return Number.isFinite(normalizedValue) && normalizedValue > 0;
}

function hasBuildRunEventRecordValue(
  value?: BuildLiveRunEvent['details'] | BuildLiveRunEvent['usage']
) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
  );
}

function resolveBuildRunEventStringValue(
  nextValue?: string | null,
  currentValue?: string | null
) {
  if (hasBuildRunEventStringValue(nextValue)) return nextValue || null;
  return hasBuildRunEventStringValue(currentValue)
    ? currentValue || null
    : null;
}

function resolveBuildRunEventNumberValue(
  nextValue?: number | null,
  currentValue?: number | null
) {
  if (hasBuildRunEventNumberValue(nextValue)) return Number(nextValue);
  return hasBuildRunEventNumberValue(currentValue)
    ? Number(currentValue)
    : null;
}

function isSparseStableBuildRunEventDuplicate(event: BuildLiveRunEvent) {
  return (
    !hasBuildRunEventNumberValue(event.schemaVersion) &&
    !hasBuildRunEventStringValue(event.eventType) &&
    !hasBuildRunEventStringValue(event.source) &&
    !hasBuildRunEventStringValue(event.threadId) &&
    !hasBuildRunEventNumberValue(event.sequence) &&
    !hasBuildRunEventRecordValue(event.details) &&
    !hasBuildRunEventRecordValue(event.usage)
  );
}

function mergeStableBuildRunEventDuplicate({
  currentEvent,
  nextEvent
}: {
  currentEvent: BuildLiveRunEvent;
  nextEvent: BuildLiveRunEvent;
}): BuildLiveRunEvent {
  const nextEventIsSparse = isSparseStableBuildRunEventDuplicate(nextEvent);
  const nextDetails = hasBuildRunEventRecordValue(nextEvent.details)
    ? nextEvent.details
    : null;
  const mergedDetails = nextDetails?.thoughtContentIsDelta
    ? {
        ...(currentEvent.details || {}),
        ...nextDetails,
        thoughtContent: `${currentEvent.details?.thoughtContent || ''}${
          nextDetails.thoughtContent || ''
        }`,
        thoughtContentIsDelta: false
      }
    : nextDetails;
  return {
    ...currentEvent,
    ...nextEvent,
    id: currentEvent.id,
    schemaVersion: resolveBuildRunEventNumberValue(
      nextEvent.schemaVersion,
      currentEvent.schemaVersion
    ),
    eventType: resolveBuildRunEventStringValue(
      nextEvent.eventType,
      currentEvent.eventType
    ),
    source: resolveBuildRunEventStringValue(
      nextEvent.source,
      currentEvent.source
    ),
    threadId: resolveBuildRunEventStringValue(
      nextEvent.threadId,
      currentEvent.threadId
    ),
    requestId: resolveBuildRunEventStringValue(
      nextEvent.requestId,
      currentEvent.requestId
    ),
    sequence: resolveBuildRunEventNumberValue(
      nextEvent.sequence,
      currentEvent.sequence
    ),
    buildId: resolveBuildRunEventNumberValue(
      nextEvent.buildId,
      currentEvent.buildId
    ),
    userId: resolveBuildRunEventNumberValue(
      nextEvent.userId,
      currentEvent.userId
    ),
    phase: resolveBuildRunEventStringValue(nextEvent.phase, currentEvent.phase),
    deduped: nextEventIsSparse
      ? Boolean(nextEvent.deduped || currentEvent.deduped)
      : nextEvent.deduped,
    details: mergedDetails
      ? mergedDetails
      : (currentEvent.details ?? null),
    usage: hasBuildRunEventRecordValue(nextEvent.usage)
      ? nextEvent.usage
      : (currentEvent.usage ?? null)
  };
}

function normalizeBuildRunDeferredRequest(
  deferredBuildRequest?: BuildLiveRunDeferredRequest | null
) {
  const message = String(deferredBuildRequest?.message || '').trim();
  if (!message) return null;
  const messageContext =
    String(deferredBuildRequest?.messageContext || '').trim() || null;
  const planAction =
    deferredBuildRequest?.planAction === 'continue' ||
    deferredBuildRequest?.planAction === 'cancel' ||
    deferredBuildRequest?.planAction === 'pivot' ||
    deferredBuildRequest?.planAction === 'start' ||
    deferredBuildRequest?.planAction === 'smaller' ||
    deferredBuildRequest?.planAction === 'skip'
      ? deferredBuildRequest.planAction
      : null;
  const stopRequestId =
    String(deferredBuildRequest?.stopRequestId || '').trim() || null;
  return {
    message,
    messageContext,
    planAction,
    stopActiveRun:
      typeof deferredBuildRequest?.stopActiveRun === 'boolean'
        ? deferredBuildRequest.stopActiveRun
        : null,
    stopRequestId
  };
}

function resolveBuildRunUpdatedAt(
  updatedAt?: number | null,
  fallbackUpdatedAt?: number
) {
  const normalizedUpdatedAt = Number(updatedAt || 0);
  if (normalizedUpdatedAt > 0) {
    return normalizedUpdatedAt;
  }
  const normalizedFallbackUpdatedAt = Number(fallbackUpdatedAt || 0);
  return normalizedFallbackUpdatedAt > 0
    ? normalizedFallbackUpdatedAt
    : Date.now();
}

export function applyBuildRunStreamUpdate(
  currentRun: BuildLiveRunState,
  buildRun?: BuildLiveRunStreamUpdatePayload | null,
  options?: {
    updatedAt?: number | null;
  }
): BuildLiveRunState {
  const hasStatusField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'status'
  );
  const hasUserMessageContentField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'userMessageContent'
  );
  const hasUserClientMessageIdField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'userClientMessageId'
  );
  const hasAssistantClientMessageIdField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'assistantClientMessageId'
  );
  const hasAssistantStatusStepsField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'assistantStatusSteps'
  );
  const hasCodeGeneratedField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'codeGenerated'
  ) || Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'codeGeneratedDelta'
  );
  const hasUsageMetricsField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'usageMetrics'
  );
  const hasBaseProjectFilesField = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'baseProjectFiles'
  );
  const normalizedBaseProjectFiles = hasBaseProjectFilesField
    ? normalizeBuildRunProjectFiles(
        Array.isArray(buildRun?.baseProjectFiles)
          ? buildRun.baseProjectFiles
          : []
      )
    : null;
  const projectFilesMode =
    buildRun?.projectFilesMode === 'snapshot' ? 'snapshot' : 'patch';
  const incomingProjectFiles = Array.isArray(buildRun?.projectFiles)
    ? buildRun.projectFiles
    : null;
  const hasProjectFilesField = incomingProjectFiles !== null;
  const hasProjectFileUpdates =
    hasProjectFilesField &&
    (projectFilesMode === 'snapshot' ||
      incomingProjectFiles.length > 0 ||
      buildRun?.projectFilesPersisted === true);
  const normalizedProjectFiles = hasProjectFilesField
    ? normalizeBuildRunProjectFiles(incomingProjectFiles)
    : null;
  const hasExplicitProjectFilesFocusPath = Object.prototype.hasOwnProperty.call(
    buildRun || {},
    'projectFilesFocusPath'
  );
  const explicitProjectFilesFocusPath = hasExplicitProjectFilesFocusPath
    ? typeof buildRun?.projectFilesFocusPath === 'string'
      ? normalizeBuildRunProjectFilePath(buildRun.projectFilesFocusPath)
      : null
    : undefined;
  const projectFilesPersisted = buildRun?.projectFilesPersisted === true;
  const persistedUserMessageId =
    Number(buildRun?.userMessageId || 0) > 0
      ? Number(buildRun?.userMessageId)
      : null;
  const persistedAssistantMessageId =
    Number(buildRun?.assistantMessageId || 0) > 0
      ? Number(buildRun?.assistantMessageId)
      : null;
  const persistedAssistantMessageCreatedAt =
    Number(buildRun?.assistantMessageCreatedAt || 0) > 0
      ? Number(buildRun?.assistantMessageCreatedAt)
      : null;
  const normalizedUserMessageContent = hasUserMessageContentField
    ? typeof buildRun?.userMessageContent === 'string'
      ? buildRun.userMessageContent
      : ''
    : null;
  const normalizedUserClientMessageId = hasUserClientMessageIdField
    ? normalizeBuildRunClientMessageId(buildRun?.userClientMessageId)
    : null;
  const normalizedAssistantClientMessageId = hasAssistantClientMessageIdField
    ? normalizeBuildRunClientMessageId(buildRun?.assistantClientMessageId)
    : null;
  const latestAssistantStatusStep = Array.isArray(
    buildRun?.assistantStatusSteps
  )
    ? String(
        buildRun.assistantStatusSteps[
          buildRun.assistantStatusSteps.length - 1
        ] || ''
      ).trim() || null
    : null;
  const nextStatus = hasStatusField
    ? typeof buildRun?.status === 'string'
      ? buildRun.status
      : null
    : hasAssistantStatusStepsField
      ? latestAssistantStatusStep
      : currentRun.status;
  const nextAssistantStatusSteps = hasAssistantStatusStepsField
    ? normalizeBuildRunAssistantStatusSteps(
        buildRun?.assistantStatusSteps,
        nextStatus
      )
    : hasStatusField
      ? appendAssistantStatusStep(currentRun.assistantStatusSteps, nextStatus)
      : currentRun.assistantStatusSteps;
  const nextBaseProjectFiles = hasBaseProjectFilesField
    ? normalizedBaseProjectFiles || []
    : projectFilesPersisted && normalizedProjectFiles
      ? normalizedProjectFiles
      : currentRun.baseProjectFiles;
  const nextStreamingProjectFiles = hasProjectFileUpdates
    ? projectFilesMode === 'snapshot'
      ? normalizedProjectFiles
      : overlayBuildRunProjectFiles({
          baseFiles:
            currentRun.streamingProjectFiles !== null
              ? currentRun.streamingProjectFiles
              : hasBaseProjectFilesField
                ? nextBaseProjectFiles
                : currentRun.baseProjectFiles,
          updates: normalizedProjectFiles
        })
    : currentRun.streamingProjectFiles;

  return {
    ...currentRun,
    generating: true,
    terminalState: null,
    stopReason: null,
    interruptionReason: null,
    error: null,
    status: nextStatus,
    assistantStatusSteps: nextAssistantStatusSteps,
    userMessage: currentRun.userMessage
      ? {
          ...currentRun.userMessage,
          ...(persistedUserMessageId
            ? {
                id: persistedUserMessageId,
                persisted: true
              }
            : {}),
          ...(hasUserMessageContentField
            ? {
                content:
                  normalizedUserMessageContent || currentRun.userMessage.content
              }
            : {}),
          ...(hasUserClientMessageIdField
            ? {
                clientMessageId: normalizedUserClientMessageId
              }
            : {})
        }
      : persistedUserMessageId !== null ||
          hasUserMessageContentField ||
          hasUserClientMessageIdField
        ? {
            id: persistedUserMessageId || createFallbackBuildRunMessageId(),
            role: 'user' as const,
            content: normalizedUserMessageContent || '',
            codeGenerated: null,
            billingState: null,
            artifactVersionId: null,
            clientMessageId: normalizedUserClientMessageId,
            createdAt: Math.floor(Date.now() / 1000),
            persisted: persistedUserMessageId !== null
          }
        : currentRun.userMessage,
    assistantMessage: currentRun.assistantMessage
      ? {
          ...currentRun.assistantMessage,
          id: persistedAssistantMessageId || currentRun.assistantMessage.id,
          persisted:
            persistedAssistantMessageId !== null
              ? true
              : currentRun.assistantMessage.persisted,
          content:
            typeof buildRun?.reply === 'string'
              ? buildRun.reply
              : typeof buildRun?.replyDelta === 'string'
                ? `${currentRun.assistantMessage.content}${buildRun.replyDelta}`
              : currentRun.assistantMessage.content,
          createdAt:
            persistedAssistantMessageCreatedAt ||
            currentRun.assistantMessage.createdAt,
          ...(hasCodeGeneratedField
            ? {
                streamCodePreview:
                  typeof buildRun?.codeGeneratedDelta === 'string'
                    ? `${currentRun.assistantMessage.streamCodePreview || ''}${
                        buildRun.codeGeneratedDelta
                      }`
                    : buildRun?.codeGenerated ?? null
              }
            : {}),
          ...(hasAssistantClientMessageIdField
            ? {
                clientMessageId: normalizedAssistantClientMessageId
              }
            : {})
        }
      : persistedAssistantMessageId ||
          typeof buildRun?.reply === 'string' ||
          typeof buildRun?.replyDelta === 'string' ||
          hasAssistantClientMessageIdField
        ? {
            id:
              persistedAssistantMessageId || createFallbackBuildRunMessageId(),
            role: 'assistant' as const,
            content:
              typeof buildRun?.reply === 'string'
                ? buildRun.reply
                : typeof buildRun?.replyDelta === 'string'
                  ? buildRun.replyDelta
                  : '',
            codeGenerated: null,
            ...(hasCodeGeneratedField
              ? {
                  streamCodePreview:
                    typeof buildRun?.codeGeneratedDelta === 'string'
                      ? buildRun.codeGeneratedDelta
                      : buildRun?.codeGenerated ?? null
                }
              : {}),
            billingState: null,
            artifactVersionId: null,
            clientMessageId: normalizedAssistantClientMessageId,
            createdAt:
              persistedAssistantMessageCreatedAt ||
              Math.floor(Date.now() / 1000),
            persisted: persistedAssistantMessageId !== null
          }
        : currentRun.assistantMessage,
    usageMetrics: hasUsageMetricsField
      ? normalizeBuildRunUsageMetrics(buildRun?.usageMetrics)
      : currentRun.usageMetrics,
    baseProjectFiles: nextBaseProjectFiles,
    streamingProjectFiles: projectFilesPersisted
      ? null
      : nextStreamingProjectFiles,
    streamingFocusFilePath: projectFilesPersisted
      ? null
      : hasExplicitProjectFilesFocusPath
        ? (explicitProjectFilesFocusPath ?? null)
        : hasProjectFileUpdates
          ? getStreamingFocusFilePath(buildRun?.projectFiles)
          : currentRun.streamingFocusFilePath,
    executionPlan: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'executionPlan'
    )
      ? (buildRun?.executionPlan ?? null)
      : currentRun.executionPlan,
    followUpPrompt: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'followUpPrompt'
    )
      ? (buildRun?.followUpPrompt ?? null)
      : currentRun.followUpPrompt,
    pendingToolApproval: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'pendingToolApproval'
    )
      ? (buildRun?.pendingToolApproval ?? null)
      : currentRun.pendingToolApproval,
    thumbnailNudge: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'thumbnailNudge'
    )
      ? (buildRun?.thumbnailNudge ?? null)
      : currentRun.thumbnailNudge,
    deferredBuildRequest: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'deferredBuildRequest'
    )
      ? normalizeBuildRunDeferredRequest(buildRun?.deferredBuildRequest)
      : currentRun.deferredBuildRequest,
    runtimeExplorationPlan: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'runtimeExplorationPlan'
    )
      ? (buildRun?.runtimeExplorationPlan ?? null)
      : currentRun.runtimeExplorationPlan,
    runtimePlanRefined:
      typeof buildRun?.runtimePlanRefined === 'boolean'
        ? buildRun.runtimePlanRefined
        : currentRun.runtimePlanRefined,
    billingState: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'billingState'
    )
      ? (buildRun?.billingState ?? null)
      : currentRun.billingState,
    requestLimits: Object.prototype.hasOwnProperty.call(
      buildRun || {},
      'requestLimits'
    )
      ? (buildRun?.requestLimits ?? null)
      : currentRun.requestLimits,
    updatedAt: resolveBuildRunUpdatedAt(options?.updatedAt)
  };
}

function upsertBuildRun(
  state: BuildState,
  buildId: number,
  nextRun: BuildLiveRunState
) {
  return {
    ...state,
    buildRuns: {
      ...state.buildRuns,
      [getBuildRunKey(buildId)]: nextRun
    },
    buildRunRequestMap: nextRun.requestId
      ? {
          ...state.buildRunRequestMap,
          [nextRun.requestId]: buildId
        }
      : state.buildRunRequestMap
  };
}

function removeBuildRunRequestMapping(
  requestMap: Record<string, number>,
  requestId: string
) {
  if (!requestId || !requestMap[requestId]) return requestMap;
  const nextMap = { ...requestMap };
  delete nextMap[requestId];
  return nextMap;
}

export default function BuildReducer(
  state: BuildState,
  action: BuildAction
): BuildState {
  switch (action.type) {
    case 'UPSERT_BUILD_SUMMARIES': {
      const values =
        action.buildSummary?.builds !== undefined
          ? action.buildSummary.builds
          : action.buildSummary?.build;
      const buildsById = mergeBuildSummaryMap(state.buildsById || {}, values);
      return buildsById === state.buildsById
        ? state
        : {
            ...state,
            buildsById
          };
    }
    case 'PATCH_BUILD_SUMMARY': {
      const buildsById = patchBuildSummaryMap(
        state.buildsById || {},
        Number(action.buildSummary?.buildId || 0),
        action.buildSummary?.patch || {}
      );
      return buildsById === state.buildsById
        ? state
        : {
            ...state,
            buildsById
          };
    }
    case 'REMOVE_BUILD_SUMMARY': {
      const buildsById = removeBuildSummaryFromMap(
        state.buildsById || {},
        Number(action.buildSummary?.buildId || 0)
      );
      return buildsById === state.buildsById
        ? state
        : {
            ...state,
            buildsById
          };
    }
    case 'REGISTER_BUILD_RUN': {
      const buildId = Number(action.buildRun?.buildId || 0);
      const requestId = String(action.buildRun?.requestId || '').trim();
      if (!buildId || !requestId) return state;
      const nextStatus =
        typeof action.buildRun?.status === 'string'
          ? action.buildRun.status
          : null;
      const nextAssistantStatusSteps = normalizeBuildRunAssistantStatusSteps(
        action.buildRun?.assistantStatusSteps,
        nextStatus
      );
      return upsertBuildRun(state, buildId, {
        buildId,
        requestId,
        runMode: action.buildRun?.runMode || 'user',
        generating: action.buildRun?.generating !== false,
        terminalState: null,
        stopReason: null,
        interruptionReason: null,
        error:
          typeof action.buildRun?.error === 'string' &&
          action.buildRun.error.trim()
            ? action.buildRun.error
            : null,
        status: nextStatus,
        assistantStatusSteps: nextAssistantStatusSteps,
        agentContext: normalizeBuildRunRecord(action.buildRun?.agentContext),
        lifecycle: normalizeBuildRunRecord(action.buildRun?.lifecycle),
        usageMetrics: {},
        runEvents: [],
        userMessage: action.buildRun?.userMessage || null,
        assistantMessage: action.buildRun?.assistantMessage || null,
        baseProjectFiles: normalizeBuildRunProjectFiles(
          action.buildRun?.baseProjectFiles
        ),
        ...(typeof action.buildRun?.projectFilesHash === 'string' &&
        action.buildRun.projectFilesHash.trim()
          ? { projectFilesHash: action.buildRun.projectFilesHash.trim() }
          : {}),
        streamingProjectFiles: null,
        streamingFocusFilePath: null,
        executionPlan: null,
        followUpPrompt: null,
        pendingToolApproval: null,
        thumbnailNudge: null,
        deferredBuildRequest: null,
        runtimeExplorationPlan: null,
        runtimePlanRefined: false,
        billingState: null,
        updatedAt:
          Number(action.buildRun?.updatedAt || 0) > 0
            ? Number(action.buildRun?.updatedAt)
            : Date.now()
      });
    }
    case 'UPDATE_BUILD_RUN_STATUS': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextStatus =
        typeof action.buildRun?.status === 'string'
          ? action.buildRun.status
          : null;
      return upsertBuildRun(state, buildId, {
        ...currentRun,
        generating: true,
        terminalState: null,
        stopReason: null,
        interruptionReason: null,
        error: null,
        status: nextStatus,
        assistantStatusSteps: appendAssistantStatusStep(
          currentRun.assistantStatusSteps,
          nextStatus
        ),
        updatedAt: Date.now()
      });
    }
    case 'UPDATE_BUILD_RUN_STREAM': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextUpdatedAt = resolveBuildRunUpdatedAt(
        action.buildRun?.updatedAt
      );
      return upsertBuildRun(
        state,
        buildId,
        applyBuildRunStreamUpdate(currentRun, action.buildRun, {
          updatedAt: nextUpdatedAt
        })
      );
    }
    case 'APPLY_BUILD_RUN_RUNNING_SNAPSHOT': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      const runningSnapshot = action.buildRun?.runningSnapshot;
      if (!currentRun || !runningSnapshot) return state;
      const nextStatus =
        typeof runningSnapshot.status === 'string'
          ? runningSnapshot.status
          : null;
      const nextUpdatedAt = resolveBuildRunUpdatedAt(
        runningSnapshot.updatedAt,
        currentRun.updatedAt
      );
      const nextRunWithoutStreamUpdate: BuildLiveRunState = {
        ...currentRun,
        generating: true,
        terminalState: null,
        stopReason: null,
        interruptionReason: null,
        error: null,
        status: nextStatus,
        assistantStatusSteps: normalizeBuildRunAssistantStatusSteps(
          runningSnapshot.assistantStatusSteps,
          nextStatus
        ),
        agentContext: Object.prototype.hasOwnProperty.call(
          runningSnapshot,
          'agentContext'
        )
          ? normalizeBuildRunRecord(runningSnapshot.agentContext)
          : currentRun.agentContext || null,
        lifecycle: Object.prototype.hasOwnProperty.call(
          runningSnapshot,
          'lifecycle'
        )
          ? normalizeBuildRunRecord(runningSnapshot.lifecycle)
          : currentRun.lifecycle || null,
        usageMetrics: normalizeBuildRunUsageMetrics(
          runningSnapshot.usageMetrics
        ),
        updatedAt: nextUpdatedAt
      };
      return upsertBuildRun(state, buildId, nextRunWithoutStreamUpdate);
    }
    case 'APPEND_BUILD_RUN_EVENT': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId || !action.buildRun?.event) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextEvent = action.buildRun.event;
      const nextEventIdentity = getBuildRunEventLogicalIdentity(nextEvent);
      let nextRunEvents: BuildLiveRunEvent[];

      if (nextEventIdentity.hasStableId) {
        const existingEventIndex = currentRun.runEvents.findIndex((event) => {
          const existingEventIdentity = getBuildRunEventLogicalIdentity(event);
          return (
            existingEventIdentity.hasStableId &&
            existingEventIdentity.key === nextEventIdentity.key
          );
        });

        nextRunEvents =
          existingEventIndex >= 0
            ? currentRun.runEvents.map((event, index) => {
                if (index !== existingEventIndex) {
                  return event;
                }
                return mergeStableBuildRunEventDuplicate({
                  currentEvent: event,
                  nextEvent
                });
              })
            : [...currentRun.runEvents, nextEvent].slice(-40);
      } else {
        const lastEvent = currentRun.runEvents[currentRun.runEvents.length - 1];
        const normalizedNextEventCreatedAt = normalizeBuildRunEventCreatedAt(
          nextEvent.createdAt
        );
        const normalizedLastEventCreatedAt = normalizeBuildRunEventCreatedAt(
          lastEvent?.createdAt
        );

        nextRunEvents =
          lastEvent &&
          lastEvent.kind === nextEvent.kind &&
          lastEvent.phase === nextEvent.phase &&
          lastEvent.message === nextEvent.message &&
          Math.abs(
            normalizedLastEventCreatedAt - normalizedNextEventCreatedAt
          ) < 1500
            ? [
                ...currentRun.runEvents.slice(0, -1),
                {
                  ...lastEvent,
                  ...nextEvent,
                  id: lastEvent.id,
                  createdAt:
                    normalizedNextEventCreatedAt || lastEvent.createdAt,
                  deduped: nextEvent.deduped,
                  details: nextEvent.details ?? lastEvent.details ?? null,
                  usage: nextEvent.usage ?? lastEvent.usage ?? null
                }
              ]
            : [...currentRun.runEvents, nextEvent].slice(-40);
      }
      const lifecycleErrorMessage =
        nextEvent.kind === 'lifecycle' &&
        String(nextEvent.phase || '')
          .trim()
          .toLowerCase() === 'error'
          ? String(nextEvent.message || '')
              .replace(/^.*?failed:\s*/i, '')
              .trim()
          : '';
      return upsertBuildRun(state, buildId, {
        ...currentRun,
        runEvents: nextRunEvents,
        error: lifecycleErrorMessage || currentRun.error,
        updatedAt: resolveBuildRunUpdatedAt(action.buildRun?.updatedAt)
      });
    }
    case 'COMPLETE_BUILD_RUN': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextRequestMap = removeBuildRunRequestMapping(
        state.buildRunRequestMap,
        currentRun.requestId
      );
      const nextRun = {
        ...currentRun,
        generating: false,
        terminalState: 'complete' as const,
        stopReason: null,
        interruptionReason:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'interruptionReason'
          )
            ? (action.buildRun.interruptionReason ?? null)
            : (currentRun.interruptionReason ?? null),
        error: null,
        status: null,
        assistantStatusSteps: [],
        lifecycle:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(action.buildRun, 'lifecycle')
            ? normalizeBuildRunRecord(action.buildRun.lifecycle)
            : currentRun.lifecycle || null,
        userMessage:
          currentRun.userMessage &&
          Number(action.buildRun?.persistedUserId || 0) > 0
            ? {
                ...currentRun.userMessage,
                id: Number(action.buildRun?.persistedUserId),
                ...(Object.prototype.hasOwnProperty.call(
                  action.buildRun || {},
                  'userClientMessageId'
                )
                  ? {
                      clientMessageId: normalizeBuildRunClientMessageId(
                        action.buildRun?.userClientMessageId
                      )
                    }
                  : {}),
                persisted: true
              }
            : currentRun.userMessage,
        assistantMessage: currentRun.assistantMessage
          ? {
              ...currentRun.assistantMessage,
              id:
                Number(action.buildRun?.persistedAssistantId || 0) > 0
                  ? Number(action.buildRun?.persistedAssistantId)
                  : currentRun.assistantMessage.id,
              ...(Object.prototype.hasOwnProperty.call(
                action.buildRun || {},
                'assistantClientMessageId'
              )
                ? {
                    clientMessageId: normalizeBuildRunClientMessageId(
                      action.buildRun?.assistantClientMessageId
                    )
                  }
                : {}),
              persisted:
                Number(action.buildRun?.persistedAssistantId || 0) > 0
                  ? true
                  : currentRun.assistantMessage.persisted,
              content:
                typeof action.buildRun?.assistantText === 'string'
                  ? action.buildRun.assistantText
                  : currentRun.assistantMessage.content,
              codeGenerated:
                action.buildRun &&
                Object.prototype.hasOwnProperty.call(
                  action.buildRun,
                  'artifactCode'
                )
                  ? (action.buildRun?.artifactCode ?? null)
                  : currentRun.assistantMessage.codeGenerated,
              billingState:
                action.buildRun &&
                Object.prototype.hasOwnProperty.call(
                  action.buildRun,
                  'billingState'
                )
                  ? (action.buildRun.billingState ?? null)
                  : (currentRun.assistantMessage.billingState ?? null),
              streamCodePreview: null,
              artifactVersionId:
                Number(action.buildRun?.artifactVersionId || 0) > 0
                  ? Number(action.buildRun?.artifactVersionId)
                  : (currentRun.assistantMessage.artifactVersionId ?? null),
              createdAt:
                Number(action.buildRun?.createdAt || 0) > 0
                  ? Number(action.buildRun?.createdAt)
                  : currentRun.assistantMessage.createdAt
            }
          : currentRun.assistantMessage,
        baseProjectFiles:
          Array.isArray(action.buildRun?.projectFiles) &&
          action.buildRun.projectFiles.length > 0
            ? normalizeBuildRunProjectFiles(action.buildRun.projectFiles)
            : action.buildRun?.workspaceChanged === false
              ? currentRun.baseProjectFiles
              : currentRun.streamingProjectFiles !== null
                ? currentRun.streamingProjectFiles
                : currentRun.baseProjectFiles,
        projectFilesHash:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'projectFilesHash'
          )
            ? typeof action.buildRun.projectFilesHash === 'string' &&
              action.buildRun.projectFilesHash.trim()
              ? action.buildRun.projectFilesHash.trim()
              : null
            : (currentRun.projectFilesHash ?? null),
        streamingProjectFiles: null,
        streamingFocusFilePath: null,
        executionPlan:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(action.buildRun, 'executionPlan')
            ? (action.buildRun.executionPlan ?? null)
            : currentRun.executionPlan,
        followUpPrompt:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'followUpPrompt'
          )
            ? (action.buildRun.followUpPrompt ?? null)
            : currentRun.followUpPrompt,
        pendingToolApproval:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'pendingToolApproval'
          )
            ? (action.buildRun.pendingToolApproval ?? null)
            : (currentRun.pendingToolApproval ?? null),
        thumbnailNudge:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'thumbnailNudge'
          )
            ? (action.buildRun.thumbnailNudge ?? null)
            : (currentRun.thumbnailNudge ?? null),
        deferredBuildRequest:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'deferredBuildRequest'
          )
            ? normalizeBuildRunDeferredRequest(
                action.buildRun.deferredBuildRequest
              )
            : currentRun.deferredBuildRequest,
        runtimeExplorationPlan:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(
            action.buildRun,
            'runtimeExplorationPlan'
          )
            ? (action.buildRun.runtimeExplorationPlan ?? null)
            : currentRun.runtimeExplorationPlan,
        runtimePlanRefined:
          typeof action.buildRun?.runtimePlanRefined === 'boolean'
            ? action.buildRun.runtimePlanRefined
            : currentRun.runtimePlanRefined,
        billingState:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(action.buildRun, 'billingState')
            ? (action.buildRun.billingState ?? null)
            : currentRun.billingState,
        requestLimits:
          action.buildRun &&
          Object.prototype.hasOwnProperty.call(action.buildRun, 'requestLimits')
            ? (action.buildRun.requestLimits ?? null)
            : currentRun.requestLimits,
        updatedAt: Date.now()
      };
      return {
        ...state,
        buildRuns: {
          ...state.buildRuns,
          [key]: nextRun
        },
        buildRunRequestMap: nextRequestMap
      };
    }
    case 'FAIL_BUILD_RUN': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextRequestMap = removeBuildRunRequestMapping(
        state.buildRunRequestMap,
        currentRun.requestId
      );
      const errorMessage =
        String(action.buildRun?.error || '').trim() ||
        'Lumine had trouble with that request. Please try again.';
      const preserveTransientUserMessage =
        action.buildRun?.preserveTransientUserMessage === true;
      const preserveTransientAssistantMessage =
        action.buildRun?.preserveTransientAssistantMessage === true;
      const preserveAssistantArtifactsOnError =
        action.buildRun?.preserveAssistantArtifactsOnError === true ||
        (currentRun.assistantMessage?.persisted === true &&
          (Number(currentRun.assistantMessage.artifactVersionId || 0) > 0 ||
            (typeof currentRun.assistantMessage.codeGenerated === 'string' &&
              currentRun.assistantMessage.codeGenerated.trim().length > 0)));
      const nextAssistantText =
        typeof action.buildRun?.assistantText === 'string'
          ? action.buildRun.assistantText
          : errorMessage;
      return {
        ...state,
        buildRuns: {
          ...state.buildRuns,
          [key]: {
            ...currentRun,
            generating: false,
            terminalState: 'error' as const,
            stopReason: null,
            interruptionReason: null,
            error: errorMessage,
            status: null,
            assistantStatusSteps: [],
            lifecycle:
              action.buildRun &&
              Object.prototype.hasOwnProperty.call(action.buildRun, 'lifecycle')
                ? normalizeBuildRunRecord(action.buildRun.lifecycle)
                : currentRun.lifecycle || null,
            userMessage:
              currentRun.userMessage?.persisted || preserveTransientUserMessage
                ? currentRun.userMessage
                : null,
            assistantMessage:
              currentRun.assistantMessage?.persisted ||
              preserveTransientAssistantMessage
                ? currentRun.assistantMessage
                  ? {
                      ...currentRun.assistantMessage,
                      content: preserveAssistantArtifactsOnError
                        ? currentRun.assistantMessage.content
                        : nextAssistantText,
                      codeGenerated: preserveAssistantArtifactsOnError
                        ? (currentRun.assistantMessage.codeGenerated ?? null)
                        : null,
                      streamCodePreview: null,
                      artifactVersionId: preserveAssistantArtifactsOnError
                        ? (currentRun.assistantMessage.artifactVersionId ??
                          null)
                        : null
                    }
                  : null
                : null,
            streamingProjectFiles: null,
            streamingFocusFilePath: null,
            requestLimits:
              action.buildRun &&
              Object.prototype.hasOwnProperty.call(
                action.buildRun,
                'requestLimits'
              )
                ? (action.buildRun.requestLimits ?? null)
                : currentRun.requestLimits,
            updatedAt: Date.now()
          }
        },
        buildRunRequestMap: nextRequestMap
      };
    }
    case 'STOP_BUILD_RUN': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextRequestMap = removeBuildRunRequestMapping(
        state.buildRunRequestMap,
        currentRun.requestId
      );
      const preserveTransientUserMessage =
        action.buildRun?.preserveTransientUserMessage === true;
      const preserveTransientAssistantMessage =
        action.buildRun?.preserveTransientAssistantMessage === true;
      const stopReason =
        action.buildRun?.stopReason === 'replacement'
          ? 'replacement'
          : action.buildRun?.stopReason === 'user'
            ? 'user'
            : null;
      const shouldClearAssistantMessage = stopReason === 'replacement';
      return {
        ...state,
        buildRuns: {
          ...state.buildRuns,
          [key]: {
            ...currentRun,
            generating: false,
            terminalState: 'stopped' as const,
            stopReason,
            interruptionReason: null,
            error: null,
            status: null,
            assistantStatusSteps: [],
            lifecycle:
              action.buildRun &&
              Object.prototype.hasOwnProperty.call(action.buildRun, 'lifecycle')
                ? normalizeBuildRunRecord(action.buildRun.lifecycle)
                : currentRun.lifecycle || null,
            userMessage:
              currentRun.userMessage?.persisted || preserveTransientUserMessage
                ? currentRun.userMessage
                : null,
            assistantMessage:
              !shouldClearAssistantMessage &&
              (currentRun.assistantMessage?.persisted ||
                preserveTransientAssistantMessage)
                ? currentRun.assistantMessage
                  ? {
                      ...currentRun.assistantMessage,
                      content:
                        typeof action.buildRun?.assistantText === 'string'
                          ? action.buildRun.assistantText
                          : currentRun.assistantMessage.content,
                      codeGenerated: null,
                      streamCodePreview: null,
                      artifactVersionId: null
                    }
                  : null
                : null,
            streamingProjectFiles: null,
            streamingFocusFilePath: null,
            updatedAt: Date.now()
          }
        },
        buildRunRequestMap: nextRequestMap
      };
    }
    case 'REMOVE_BUILD_RUN_MESSAGE': {
      const buildId = getBuildRunLookupBuildId(state, action.buildRun);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const messageId = Number(action.buildRun?.messageId || 0);
      const messageRole = action.buildRun?.messageRole || null;
      const clientMessageId = normalizeBuildRunClientMessageId(
        action.buildRun?.clientMessageId
      );
      function matchesMessage(message: BuildLiveRunMessage | null) {
        if (!message) return false;
        if (messageRole && message.role !== messageRole) return false;
        if (messageId > 0 && Number(message.id || 0) === messageId) {
          return true;
        }
        if (
          clientMessageId &&
          normalizeBuildRunClientMessageId(message.clientMessageId) ===
            clientMessageId
        ) {
          return true;
        }
        return false;
      }
      const shouldRemoveUserMessage = matchesMessage(currentRun.userMessage);
      const shouldRemoveAssistantMessage = matchesMessage(
        currentRun.assistantMessage
      );
      if (!shouldRemoveUserMessage && !shouldRemoveAssistantMessage)
        return state;
      return upsertBuildRun(state, buildId, {
        ...currentRun,
        userMessage: shouldRemoveUserMessage ? null : currentRun.userMessage,
        assistantMessage: shouldRemoveAssistantMessage
          ? null
          : currentRun.assistantMessage,
        error: shouldRemoveAssistantMessage ? null : currentRun.error,
        updatedAt: Date.now()
      });
    }
    case 'SET_BUILD_WORKSPACE': {
      const buildId = Number(action.buildRun?.buildId || 0);
      if (!buildId || !action.buildRun?.build) return state;
      const key = getBuildRunKey(buildId);
      const currentWorkspace = state.buildWorkspaces[key];
      if (
        currentWorkspace &&
        currentWorkspace.build === action.buildRun.build &&
        currentWorkspace.chatMessages === action.buildRun.chatMessages &&
        currentWorkspace.copilotPolicy === action.buildRun.copilotPolicy
      ) {
        return state;
      }
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(
          state.buildsById || {},
          action.buildRun.build
        ),
        buildWorkspaces: {
          ...state.buildWorkspaces,
          [key]: {
            build: action.buildRun.build,
            chatMessages: Array.isArray(action.buildRun.chatMessages)
              ? action.buildRun.chatMessages
              : [],
            copilotPolicy:
              action.buildRun.copilotPolicy !== undefined
                ? action.buildRun.copilotPolicy
                : null,
            updatedAt: Date.now()
          }
        }
      };
    }
    case 'REMOVE_BUILD_WORKSPACE': {
      const buildId = Number(action.buildId || 0);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      if (!(key in state.buildWorkspaces)) return state;
      const nextBuildWorkspaces = { ...state.buildWorkspaces };
      delete nextBuildWorkspaces[key];
      return {
        ...state,
        buildWorkspaces: nextBuildWorkspaces
      };
    }
    case 'SET_BUILD_WORKSPACE_COMMUNICATION_MODE': {
      const buildId = Number(action.buildWorkspaceUi?.buildId || 0);
      if (!buildId) return state;
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      const communicationMode = normalizeBuildWorkspaceCommunicationMode(
        action.buildWorkspaceUi?.communicationMode
      );
      if (currentUi.communicationMode === communicationMode) return state;
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            communicationMode
          }
        }
      };
    }
    case 'SET_BUILD_WORKSPACE_SCROLL': {
      const buildId = Number(action.buildWorkspaceUi?.buildId || 0);
      if (!buildId) return state;
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      const scrollMode = normalizeBuildWorkspaceCommunicationMode(
        action.buildWorkspaceUi?.scrollMode
      );
      const scrollTop = normalizeBuildWorkspaceScrollTop(
        action.buildWorkspaceUi?.scrollTop
      );
      if (currentUi.scrollTops[scrollMode] === scrollTop) return state;
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            scrollTops: {
              ...currentUi.scrollTops,
              [scrollMode]: scrollTop
            }
          }
        }
      };
    }
    case 'SET_BUILD_WORKSPACE_FORUM_THREAD': {
      const buildId = Number(action.buildWorkspaceUi?.buildId || 0);
      if (!buildId) return state;
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      const selectedForumThreadId = normalizeBuildWorkspaceForumThreadId(
        action.buildWorkspaceUi?.forumThreadId
      );
      if (currentUi.selectedForumThreadId === selectedForumThreadId) {
        return state;
      }
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            selectedForumThreadId
          }
        }
      };
    }
    case 'SET_BUILD_WORKSPACE_FORUM_THREADS': {
      const buildId = Number(action.buildWorkspaceForumCache?.buildId || 0);
      if (!buildId) return state;
      const scopeKey = normalizeBuildWorkspaceForumScopeKey(
        action.buildWorkspaceForumCache?.scopeKey
      );
      const threads = Array.isArray(action.buildWorkspaceForumCache?.threads)
        ? action.buildWorkspaceForumCache.threads
        : [];
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      const currentForumCache = getBuildWorkspaceForumCacheForViewer(
        currentUi,
        action.buildWorkspaceForumCache?.viewerKey
      );
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            forumCache: {
              ...currentForumCache,
              scopes: {
                ...(currentForumCache.scopes || {}),
                [scopeKey]: {
                  threads,
                  loadedAt: Date.now()
                }
              }
            }
          }
        }
      };
    }
    case 'SET_BUILD_WORKSPACE_FORUM_THREAD_DETAIL': {
      const buildId = Number(action.buildWorkspaceForumCache?.buildId || 0);
      if (!buildId) return state;
      const providedThread = action.buildWorkspaceForumCache?.thread || null;
      const threadId = normalizeBuildWorkspaceForumThreadId(
        providedThread?.id || action.buildWorkspaceForumCache?.threadId
      );
      if (!threadId) return state;
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      const currentForumCache = getBuildWorkspaceForumCacheForViewer(
        currentUi,
        action.buildWorkspaceForumCache?.viewerKey
      );
      const threadKey = getBuildWorkspaceForumThreadCacheKey(threadId);
      const currentThreadCache = currentForumCache.threadsById?.[threadKey];
      const thread = providedThread || currentThreadCache?.thread || null;
      if (!thread) return state;
      const replies = Array.isArray(action.buildWorkspaceForumCache?.replies)
        ? action.buildWorkspaceForumCache.replies
        : currentThreadCache?.replies || [];
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            forumCache: {
              ...currentForumCache,
              scopes: patchBuildWorkspaceForumThreadInScopes(
                currentForumCache.scopes || {},
                thread
              ),
              threadsById: {
                ...(currentForumCache.threadsById || {}),
                [threadKey]: {
                  thread,
                  replies,
                  loadedAt: Date.now()
                }
              }
            }
          }
        }
      };
    }
    case 'REMOVE_BUILD_WORKSPACE_FORUM_THREAD': {
      const buildId = Number(action.buildWorkspaceForumCache?.buildId || 0);
      if (!buildId) return state;
      const threadId = normalizeBuildWorkspaceForumThreadId(
        action.buildWorkspaceForumCache?.threadId
      );
      if (!threadId) return state;
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      const currentForumCache = getBuildWorkspaceForumCacheForViewer(
        currentUi,
        action.buildWorkspaceForumCache?.viewerKey
      );
      const threadKey = getBuildWorkspaceForumThreadCacheKey(threadId);
      const nextThreadsById = { ...(currentForumCache.threadsById || {}) };
      delete nextThreadsById[threadKey];
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            selectedForumThreadId:
              currentUi.selectedForumThreadId === threadId
                ? 0
                : currentUi.selectedForumThreadId,
            forumCache: {
              ...currentForumCache,
              scopes: removeBuildWorkspaceForumThreadFromScopes(
                currentForumCache.scopes || {},
                threadId
              ),
              threadsById: nextThreadsById
            }
          }
        }
      };
    }
    case 'CLEAR_BUILD_WORKSPACE_FORUM_CACHE': {
      const buildId = Number(action.buildWorkspaceForumCache?.buildId || 0);
      if (!buildId) return state;
      const key = getBuildWorkspaceUiKey(buildId);
      const currentUi = getBuildWorkspaceUiState(state, buildId);
      return {
        ...state,
        buildWorkspaceUi: {
          ...(state.buildWorkspaceUi || {}),
          [key]: {
            ...currentUi,
            selectedForumThreadId: 0,
            forumCache: createInitialBuildWorkspaceForumCacheState(
              action.buildWorkspaceForumCache?.viewerKey || ''
            )
          }
        }
      };
    }
    case 'INVALIDATE_BUILD_FORUM': {
      const rootBuildId = Number(
        action.buildForumInvalidation?.rootBuildId || 0
      );
      if (!rootBuildId) return state;
      const key = String(rootBuildId);
      return {
        ...state,
        forumInvalidationVersions: {
          ...(state.forumInvalidationVersions || {}),
          [key]: Number(state.forumInvalidationVersions?.[key] || 0) + 1
        }
      };
    }
    case 'SET_BUILD_STUDIO_SECTION': {
      const buildStudio = getBuildStudioState(state);
      const section = normalizeBuildStudioSection(action.buildStudio?.section);
      if (buildStudio.section === section) return state;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          section
        }
      };
    }
    case 'SET_BUILD_STUDIO_ACTIVE_TAB': {
      const buildStudio = getBuildStudioState(state);
      const activeTab = normalizeBuildStudioTab(action.buildStudio?.activeTab);
      if (buildStudio.activeTab === activeTab) return state;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          activeTab
        }
      };
    }
    case 'SET_BUILD_STUDIO_MY_BUILDS': {
      const buildStudio = getBuildStudioState(state);
      const userId = Number(action.buildStudio?.userId || 0) || null;
      const builds = Array.isArray(action.buildStudio?.builds)
        ? action.buildStudio.builds
        : [];
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, builds),
        buildStudio: {
          ...buildStudio,
          myBuilds: builds,
          myBuildsLoaded: true,
          myBuildsUserId: userId
        }
      };
    }
    case 'PATCH_BUILD_STUDIO_MY_BUILD': {
      const buildStudio = getBuildStudioState(state);
      const userId = Number(action.buildStudio?.userId || 0) || null;
      if (userId && buildStudio.myBuildsUserId !== userId) return state;
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(
          state.buildsById || {},
          action.buildStudio?.build
        ),
        buildStudio: {
          ...buildStudio,
          myBuilds: patchBuildStudioMyBuild(
            buildStudio.myBuilds,
            action.buildStudio?.build
          )
        }
      };
    }
    case 'REMOVE_BUILD_STUDIO_MY_BUILD': {
      const buildStudio = getBuildStudioState(state);
      const buildId = Number(action.buildStudio?.buildId || 0);
      const userId = Number(action.buildStudio?.userId || 0) || null;
      if (!buildId) return state;
      if (userId && buildStudio.myBuildsUserId !== userId) return state;
      return {
        ...state,
        buildsById: removeBuildSummaryFromMap(state.buildsById || {}, buildId),
        buildStudio: {
          ...buildStudio,
          myBuilds: buildStudio.myBuilds.filter(
            (build) => Number(build?.id || 0) !== buildId
          )
        }
      };
    }
    case 'SET_BUILD_STUDIO_TODAY_TOP_VIEWED_BUILD': {
      const buildStudio = getBuildStudioState(state);
      const quickAccess = getBuildStudioQuickAccessState(buildStudio);
      const build = action.buildStudio?.build || null;
      const builds = Array.isArray(action.buildStudio?.builds)
        ? action.buildStudio.builds
        : [];
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, [
          ...builds,
          ...(build ? [build] : [])
        ]),
        buildStudio: {
          ...buildStudio,
          quickAccess: {
            ...quickAccess,
            todayTopViewed: {
              build,
              builds,
              nextDay: Number(action.buildStudio?.nextDay) || null,
              loaded: true,
              userId: normalizeBuildStudioUserId(action.buildStudio?.userId)
            }
          }
        }
      };
    }
    case 'SET_BUILD_STUDIO_QUICK_ACCESS_BUILDS': {
      const buildStudio = getBuildStudioState(state);
      const quickAccess = getBuildStudioQuickAccessState(buildStudio);
      const mode = normalizeBuildStudioQuickAccessMode(
        action.buildStudio?.quickAccessMode
      );
      const builds = Array.isArray(action.buildStudio?.builds)
        ? action.buildStudio.builds
        : [];
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, builds),
        buildStudio: {
          ...buildStudio,
          quickAccess: {
            ...quickAccess,
            [mode]: {
              builds,
              cursor: normalizeBuildStudioQuickAccessCursor(
                action.buildStudio?.cursor
              ),
              loaded: true,
              userId: normalizeBuildStudioUserId(action.buildStudio?.userId)
            }
          }
        }
      };
    }
    case 'APPEND_BUILD_STUDIO_QUICK_ACCESS_BUILDS': {
      const buildStudio = getBuildStudioState(state);
      const quickAccess = getBuildStudioQuickAccessState(buildStudio);
      const mode = normalizeBuildStudioQuickAccessMode(
        action.buildStudio?.quickAccessMode
      );
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const currentList =
        quickAccess[mode] || createInitialBuildStudioQuickAccessListState();
      if (!currentList.loaded || currentList.userId !== userId) {
        return state;
      }
      const builds = Array.isArray(action.buildStudio?.builds)
        ? action.buildStudio.builds
        : [];
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, builds),
        buildStudio: {
          ...buildStudio,
          quickAccess: {
            ...quickAccess,
            [mode]: {
              ...currentList,
              builds: mergeBuildStudioQuickAccessItems(
                currentList.builds,
                builds
              ),
              cursor: normalizeBuildStudioQuickAccessCursor(
                action.buildStudio?.cursor
              ),
              loaded: true,
              userId
            }
          }
        }
      };
    }
    case 'REMOVE_BUILD_STUDIO_QUICK_ACCESS_BUILDS': {
      const buildStudio = getBuildStudioState(state);
      const quickAccess = getBuildStudioQuickAccessState(buildStudio);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const buildIds = new Set(
        [
          ...(Array.isArray(action.buildStudio?.buildIds)
            ? action.buildStudio.buildIds
            : []),
          action.buildStudio?.buildId
        ]
          .map((buildId) => Number(buildId || 0))
          .filter((buildId) => buildId > 0)
      );
      if (buildIds.size === 0) return state;
      const todayTopViewed = quickAccess.todayTopViewed;
      const todayTopViewedUserMatches =
        !userId || !todayTopViewed.userId || todayTopViewed.userId === userId;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          quickAccess: {
            ...quickAccess,
            recent: removeBuildsFromQuickAccessList(
              quickAccess.recent,
              buildIds,
              userId
            ),
            favorites: removeBuildsFromQuickAccessList(
              quickAccess.favorites,
              buildIds,
              userId
            ),
            todayTopViewed: todayTopViewedUserMatches
              ? {
                  ...todayTopViewed,
                  build: buildIds.has(Number(todayTopViewed.build?.id || 0))
                    ? null
                    : todayTopViewed.build,
                  builds: (todayTopViewed.builds || []).filter(
                    (build) => !buildIds.has(Number(build.id || 0))
                  )
                }
              : todayTopViewed
          }
        }
      };
    }
    case 'PATCH_BUILD_STUDIO_QUICK_ACCESS_FAVORITE': {
      const buildStudio = getBuildStudioState(state);
      const quickAccess = getBuildStudioQuickAccessState(buildStudio);
      const buildId = Number(
        action.buildStudio?.buildId || action.buildStudio?.build?.id || 0
      );
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      if (!buildId || !userId) return state;
      const isFavorited = action.buildStudio?.isFavorited === true;
      const favoritedAt = isFavorited
        ? normalizeBuildStudioNullablePositiveInteger(
            action.buildStudio?.favoritedAt
          )
        : null;
      const favoriteActivityAt = isFavorited
        ? normalizeBuildStudioNullablePositiveInteger(
            action.buildStudio?.favoriteActivityAt
          ) || favoritedAt
        : null;
      const build = {
        ...(action.buildStudio?.build || {}),
        id: buildId,
        favoriteActivityAt,
        favoritedAt,
        isFavorited
      };
      const todayTopViewed = quickAccess.todayTopViewed;
      const patchedTodayTopViewed =
        todayTopViewed.userId === userId &&
        todayTopViewed.build &&
        Number(todayTopViewed.build.id || 0) === buildId
          ? {
              ...todayTopViewed,
              build: {
                ...todayTopViewed.build,
                favoriteActivityAt,
                favoritedAt,
                isFavorited
              }
            }
          : todayTopViewed;
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, build),
        buildStudio: {
          ...buildStudio,
          quickAccess: {
            ...quickAccess,
            recent: patchQuickAccessListFavorite({
              build,
              buildId,
              favoriteActivityAt,
              favoritedAt,
              isFavorited,
              listState: quickAccess.recent,
              mode: 'recent',
              userId
            }),
            favorites: patchQuickAccessListFavorite({
              build,
              buildId,
              favoriteActivityAt,
              favoritedAt,
              isFavorited,
              listState: quickAccess.favorites,
              mode: 'favorites',
              userId
            }),
            todayTopViewed: patchedTodayTopViewed
          }
        }
      };
    }
    case 'SET_BUILD_STUDIO_BROWSE_MODE': {
      const buildStudio = getBuildStudioState(state);
      const tab = normalizeBuildStudioPublicBrowseTab(action.buildStudio?.tab);
      const browseMode = normalizeBuildStudioBrowseMode(
        action.buildStudio?.browseMode
      );
      const browseModes =
        buildStudio.browseModes || createInitialBuildStudioBrowseModes();
      if (browseModes[tab] === browseMode) return state;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          browseModes: {
            ...browseModes,
            [tab]: browseMode
          }
        }
      };
    }
    case 'SET_BUILD_STUDIO_ACTIVITY_FILTER': {
      const buildStudio = getBuildStudioState(state);
      const currentActivityPanel =
        buildStudio.activityPanel ||
        createInitialBuildStudioActivityPanelState();
      const activeTab = action.buildStudio?.activityTab
        ? normalizeBuildActivityTab(action.buildStudio.activityTab)
        : currentActivityPanel.activeTab;
      const activeSubtab = action.buildStudio?.activitySubtab
        ? normalizeBuildActivitySubtab(action.buildStudio.activitySubtab)
        : currentActivityPanel.activeSubtab;
      const resolvedActiveSubtab =
        activeTab === 'all'
          ? 'all'
          : activeSubtab === 'all'
            ? 'notifications'
            : activeSubtab;
      if (
        currentActivityPanel.activeTab === activeTab &&
        currentActivityPanel.activeSubtab === resolvedActiveSubtab
      ) {
        return state;
      }
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          activityPanel: {
            activeTab,
            activeSubtab: resolvedActiveSubtab
          }
        }
      };
    }
    case 'INVALIDATE_BUILD_STUDIO_ACTIVITY_FEEDS': {
      const buildStudio = getBuildStudioState(state);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const activityFeeds = getBuildStudioActivityFeeds(buildStudio);
      const nextActivityFeeds = Object.entries(activityFeeds).reduce(
        (result, [tab, subtabFeeds]) => {
          result[tab as BuildActivityTab] = Object.entries(subtabFeeds).reduce(
            (nextSubtabFeeds, [subtab, feed]) => {
              nextSubtabFeeds[subtab as BuildActivitySubtab] =
                !userId || feed.userId === userId
                  ? {
                      ...feed,
                      loadedAt: 0
                    }
                  : feed;
              return nextSubtabFeeds;
            },
            {} as Record<BuildActivitySubtab, BuildStudioActivityFeedState>
          );
          return result;
        },
        {} as Record<
          BuildActivityTab,
          Record<BuildActivitySubtab, BuildStudioActivityFeedState>
        >
      );
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          activityFeeds: nextActivityFeeds
        }
      };
    }
    case 'INVALIDATE_BUILD_STUDIO_BROWSE_TAB': {
      const buildStudio = getBuildStudioState(state);
      const tab = normalizeBuildStudioBrowseTab(action.buildStudio?.tab);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const browseState =
        buildStudio.browse?.[tab] || createInitialBuildStudioBrowseState();
      if (userId && browseState.userId && browseState.userId !== userId) {
        return state;
      }
      const currentCacheGeneration = normalizeBuildStudioCacheGeneration(
        browseState.cacheGeneration
      );
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          browse: {
            ...buildStudio.browse,
            [tab]: {
              ...browseState,
              loaded: false,
              cacheRefreshKey: 0,
              cacheGeneration: currentCacheGeneration + 1
            }
          }
        }
      };
    }
    case 'SET_BUILD_STUDIO_ACTIVITY_ITEMS': {
      const buildStudio = getBuildStudioState(state);
      const activityTab = normalizeBuildActivityTab(
        action.buildStudio?.activityTab
      );
      const activitySubtab = normalizeBuildActivitySubtab(
        action.buildStudio?.activitySubtab
      );
      const activityFeeds = getBuildStudioActivityFeeds(buildStudio);
      const currentTabFeeds = {
        ...createInitialBuildStudioActivityFeeds()[activityTab],
        ...(activityFeeds[activityTab] || {})
      };
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const currentFeed =
        currentTabFeeds[activitySubtab] ||
        createInitialBuildStudioActivityFeedState();
      const actionViewedPosition =
        getBuildStudioActivityViewedPositionFromPayload(action.buildStudio);
      const currentViewedPosition =
        currentFeed.userId === userId
          ? getBuildStudioActivityViewedPositionFromFeed(currentFeed)
          : actionViewedPosition;
      const nextViewedPosition =
        compareBuildStudioActivityViewedPositions(
          actionViewedPosition,
          currentViewedPosition
        ) > 0
          ? actionViewedPosition
          : currentViewedPosition;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          activityFeeds: {
            ...activityFeeds,
            [activityTab]: {
              ...currentTabFeeds,
              [activitySubtab]: {
                activities: Array.isArray(action.buildStudio?.activities)
                  ? action.buildStudio.activities
                  : [],
                loadMoreToken:
                  typeof action.buildStudio?.loadMoreToken === 'string'
                    ? action.buildStudio.loadMoreToken
                    : null,
                loaded: true,
                userId,
                loadedAt: normalizeBuildStudioActivityLoadedAt(
                  action.buildStudio?.activityLoadedAt
                ),
                ...serializeBuildStudioActivityViewedPosition(
                  nextViewedPosition
                )
              }
            }
          }
        }
      };
    }
    case 'APPEND_BUILD_STUDIO_ACTIVITY_ITEMS': {
      const buildStudio = getBuildStudioState(state);
      const activityTab = normalizeBuildActivityTab(
        action.buildStudio?.activityTab
      );
      const activitySubtab = normalizeBuildActivitySubtab(
        action.buildStudio?.activitySubtab
      );
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const activityFeeds = getBuildStudioActivityFeeds(buildStudio);
      const currentTabFeeds = {
        ...createInitialBuildStudioActivityFeeds()[activityTab],
        ...(activityFeeds[activityTab] || {})
      };
      const currentFeed =
        currentTabFeeds[activitySubtab] ||
        createInitialBuildStudioActivityFeedState();
      if (currentFeed.userId !== userId) {
        return state;
      }
      const currentViewedPosition =
        getBuildStudioActivityViewedPositionFromFeed(currentFeed);
      const actionViewedPosition =
        getBuildStudioActivityViewedPositionFromPayload(action.buildStudio);
      const nextViewedPosition =
        compareBuildStudioActivityViewedPositions(
          actionViewedPosition,
          currentViewedPosition
        ) > 0
          ? actionViewedPosition
          : currentViewedPosition;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          activityFeeds: {
            ...activityFeeds,
            [activityTab]: {
              ...currentTabFeeds,
              [activitySubtab]: {
                ...currentFeed,
                activities: mergeBuildStudioActivityItems(
                  currentFeed.activities,
                  Array.isArray(action.buildStudio?.activities)
                    ? action.buildStudio.activities
                    : []
                ),
                loadMoreToken:
                  typeof action.buildStudio?.loadMoreToken === 'string'
                    ? action.buildStudio.loadMoreToken
                    : null,
                loaded: true,
                userId,
                loadedAt: normalizeBuildStudioActivityLoadedAt(
                  action.buildStudio?.activityLoadedAt
                ),
                ...serializeBuildStudioActivityViewedPosition(
                  nextViewedPosition
                )
              }
            }
          }
        }
      };
    }
    case 'SET_BUILD_STUDIO_ACTIVITY_VIEWED': {
      const buildStudio = getBuildStudioState(state);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const actionViewedPosition =
        getBuildStudioActivityViewedPositionFromPayload(action.buildStudio);
      if (
        !userId ||
        !isValidBuildStudioActivityViewedPosition(actionViewedPosition)
      ) {
        return state;
      }
      const activityFeeds = getBuildStudioActivityFeeds(buildStudio);
      const currentAllFeeds = {
        ...createInitialBuildStudioActivityFeeds().all,
        ...(activityFeeds.all || {})
      };
      const currentAllFeed =
        currentAllFeeds.all || createInitialBuildStudioActivityFeedState();
      if (currentAllFeed.userId && currentAllFeed.userId !== userId) {
        return state;
      }
      const currentViewedPosition =
        getBuildStudioActivityViewedPositionFromFeed(currentAllFeed);
      const nextViewedPosition =
        compareBuildStudioActivityViewedPositions(
          actionViewedPosition,
          currentViewedPosition
        ) > 0
          ? actionViewedPosition
          : currentViewedPosition;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          activityFeeds: {
            ...activityFeeds,
            all: {
              ...currentAllFeeds,
              all: {
                ...currentAllFeed,
                userId,
                ...serializeBuildStudioActivityViewedPosition(
                  nextViewedPosition
                )
              }
            }
          }
        }
      };
    }
    case 'SET_BUILD_STUDIO_BROWSE_BUILDS': {
      const buildStudio = getBuildStudioState(state);
      const tab = normalizeBuildStudioBrowseTab(action.buildStudio?.tab);
      const browseMode = normalizeBuildStudioBrowseMode(
        action.buildStudio?.browseMode
      );
      const searchQuery = normalizeBuildStudioSearchQuery(
        action.buildStudio?.searchQuery
      );
      const currentTabState =
        buildStudio.browse?.[tab] || createInitialBuildStudioBrowseState();
      const currentCacheGeneration = normalizeBuildStudioCacheGeneration(
        currentTabState.cacheGeneration
      );
      const hasActionCacheGeneration =
        action.buildStudio &&
        Object.prototype.hasOwnProperty.call(
          action.buildStudio,
          'cacheGeneration'
        );
      const actionCacheGeneration = normalizeBuildStudioCacheGeneration(
        action.buildStudio?.cacheGeneration
      );
      if (
        hasActionCacheGeneration &&
        actionCacheGeneration !== currentCacheGeneration
      ) {
        return state;
      }
      const hasActionCacheRefreshKey =
        action.buildStudio &&
        Object.prototype.hasOwnProperty.call(
          action.buildStudio,
          'cacheRefreshKey'
        );
      const builds = Array.isArray(action.buildStudio?.builds)
        ? action.buildStudio.builds
        : [];
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, builds),
        buildStudio: {
          ...buildStudio,
          browse: {
            ...buildStudio.browse,
            [tab]: {
              ...currentTabState,
              builds,
              loadMoreToken:
                typeof action.buildStudio?.loadMoreToken === 'string'
                  ? action.buildStudio.loadMoreToken
                  : null,
              loaded: true,
              browseMode,
              searchQuery,
              userId: normalizeBuildStudioUserId(action.buildStudio?.userId),
              cacheRefreshKey: hasActionCacheRefreshKey
                ? normalizeBuildStudioCacheRefreshKey(
                    action.buildStudio?.cacheRefreshKey
                  )
                : currentTabState.cacheRefreshKey,
              cacheGeneration: currentCacheGeneration
            }
          }
        }
      };
    }
    case 'APPEND_BUILD_STUDIO_BROWSE_BUILDS': {
      const buildStudio = getBuildStudioState(state);
      const tab = normalizeBuildStudioBrowseTab(action.buildStudio?.tab);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const browseMode = normalizeBuildStudioBrowseMode(
        action.buildStudio?.browseMode
      );
      const searchQuery = normalizeBuildStudioSearchQuery(
        action.buildStudio?.searchQuery
      );
      const currentTabState =
        buildStudio.browse?.[tab] || createInitialBuildStudioBrowseState();
      const currentCacheGeneration = normalizeBuildStudioCacheGeneration(
        currentTabState.cacheGeneration
      );
      const hasActionCacheGeneration =
        action.buildStudio &&
        Object.prototype.hasOwnProperty.call(
          action.buildStudio,
          'cacheGeneration'
        );
      const actionCacheGeneration = normalizeBuildStudioCacheGeneration(
        action.buildStudio?.cacheGeneration
      );
      const canAppend =
        (!hasActionCacheGeneration ||
          actionCacheGeneration === currentCacheGeneration) &&
        currentTabState.userId === userId &&
        currentTabState.browseMode === browseMode &&
        currentTabState.searchQuery === searchQuery;
      if (!canAppend) return state;
      const builds = Array.isArray(action.buildStudio?.builds)
        ? action.buildStudio.builds
        : [];
      return {
        ...state,
        buildsById: mergeBuildSummaryMap(state.buildsById || {}, builds),
        buildStudio: {
          ...buildStudio,
          browse: {
            ...buildStudio.browse,
            [tab]: {
              ...currentTabState,
              builds: [...currentTabState.builds, ...builds],
              loadMoreToken:
                typeof action.buildStudio?.loadMoreToken === 'string'
                  ? action.buildStudio.loadMoreToken
                  : null,
              loaded: true,
              browseMode,
              searchQuery,
              userId
            }
          }
        }
      };
    }
    case 'SET_PROMPT_STUDIO_TAB': {
      const buildStudio = getBuildStudioState(state);
      const promptTab = normalizePromptStudioTab(action.buildStudio?.promptTab);
      if (buildStudio.promptTab === promptTab) return state;
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          promptTab
        }
      };
    }
    case 'SET_PROMPT_STUDIO_BROWSE_MODE': {
      const buildStudio = getBuildStudioState(state);
      const promptBrowseMode = normalizePromptStudioBrowseMode(
        action.buildStudio?.promptBrowseMode
      );
      if (buildStudio.promptBrowseModes?.community === promptBrowseMode) {
        return state;
      }
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          promptBrowseModes: {
            community: promptBrowseMode
          }
        }
      };
    }
    case 'INVALIDATE_PROMPT_STUDIO_TAB': {
      const buildStudio = getBuildStudioState(state);
      const promptTab = normalizePromptStudioTab(action.buildStudio?.promptTab);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const currentTabState =
        buildStudio.promptStudio?.[promptTab] ||
        createInitialPromptStudioListState();
      if (
        userId &&
        currentTabState.userId &&
        currentTabState.userId !== userId
      ) {
        return state;
      }
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          promptStudio: {
            ...buildStudio.promptStudio,
            [promptTab]: {
              ...currentTabState,
              loaded: false,
              cacheGeneration:
                normalizeBuildStudioCacheGeneration(
                  currentTabState.cacheGeneration
                ) + 1
            }
          }
        }
      };
    }
    case 'SET_PROMPT_STUDIO_ITEMS': {
      const buildStudio = getBuildStudioState(state);
      const promptTab = normalizePromptStudioTab(action.buildStudio?.promptTab);
      const currentTabState =
        buildStudio.promptStudio?.[promptTab] ||
        createInitialPromptStudioListState();
      const currentCacheGeneration = normalizeBuildStudioCacheGeneration(
        currentTabState.cacheGeneration
      );
      const hasActionCacheGeneration =
        action.buildStudio &&
        Object.prototype.hasOwnProperty.call(
          action.buildStudio,
          'cacheGeneration'
        );
      const actionCacheGeneration = normalizeBuildStudioCacheGeneration(
        action.buildStudio?.cacheGeneration
      );
      if (
        hasActionCacheGeneration &&
        actionCacheGeneration !== currentCacheGeneration
      ) {
        return state;
      }
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          promptStudio: {
            ...buildStudio.promptStudio,
            [promptTab]: {
              ...currentTabState,
              prompts: Array.isArray(action.buildStudio?.prompts)
                ? action.buildStudio.prompts
                : [],
              loadMoreToken:
                typeof action.buildStudio?.loadMoreToken === 'string'
                  ? action.buildStudio.loadMoreToken
                  : null,
              loaded: true,
              browseMode:
                promptTab === 'community'
                  ? normalizePromptStudioBrowseMode(
                      action.buildStudio?.promptBrowseMode
                    )
                  : 'recent',
              searchQuery: normalizeBuildStudioSearchQuery(
                action.buildStudio?.searchQuery
              ),
              userId: normalizeBuildStudioUserId(action.buildStudio?.userId),
              cacheGeneration: currentCacheGeneration
            }
          }
        }
      };
    }
    case 'APPEND_PROMPT_STUDIO_ITEMS': {
      const buildStudio = getBuildStudioState(state);
      const promptTab = normalizePromptStudioTab(action.buildStudio?.promptTab);
      const userId = normalizeBuildStudioUserId(action.buildStudio?.userId);
      const promptBrowseMode =
        promptTab === 'community'
          ? normalizePromptStudioBrowseMode(
              action.buildStudio?.promptBrowseMode
            )
          : 'recent';
      const searchQuery = normalizeBuildStudioSearchQuery(
        action.buildStudio?.searchQuery
      );
      const currentTabState =
        buildStudio.promptStudio?.[promptTab] ||
        createInitialPromptStudioListState();
      const currentCacheGeneration = normalizeBuildStudioCacheGeneration(
        currentTabState.cacheGeneration
      );
      const actionCacheGeneration = normalizeBuildStudioCacheGeneration(
        action.buildStudio?.cacheGeneration
      );
      if (
        actionCacheGeneration !== currentCacheGeneration ||
        currentTabState.userId !== userId ||
        currentTabState.browseMode !== promptBrowseMode ||
        currentTabState.searchQuery !== searchQuery
      ) {
        return state;
      }
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          promptStudio: {
            ...buildStudio.promptStudio,
            [promptTab]: {
              ...currentTabState,
              prompts: mergePromptStudioItems(
                currentTabState.prompts,
                Array.isArray(action.buildStudio?.prompts)
                  ? action.buildStudio.prompts
                  : []
              ),
              loadMoreToken:
                typeof action.buildStudio?.loadMoreToken === 'string'
                  ? action.buildStudio.loadMoreToken
                  : null,
              loaded: true
            }
          }
        }
      };
    }
    case 'PATCH_PROMPT_STUDIO_CLONE': {
      const buildStudio = getBuildStudioState(state);
      const promptId = Number(action.buildStudio?.prompt?.sharedTopicId || 0);
      const target = action.buildStudio?.prompt?.target;
      const channelId = Number(action.buildStudio?.prompt?.channelId || 0);
      const topicId = Number(action.buildStudio?.prompt?.topicId || 0);
      if (
        !promptId ||
        (target !== 'zero' && target !== 'ciel') ||
        !channelId ||
        !topicId
      ) {
        return state;
      }
      const nextPromptStudio = (
        ['my', 'community'] as PromptStudioTab[]
      ).reduce(
        (result, promptTab) => {
          const tabState =
            buildStudio.promptStudio?.[promptTab] ||
            createInitialPromptStudioListState();
          result[promptTab] = {
            ...tabState,
            prompts: tabState.prompts.map((prompt) => {
              const itemId = Number(prompt?.id || prompt?.subjectId || 0);
              if (itemId !== promptId) return prompt;
              const currentClones = Array.isArray(prompt?.myClones)
                ? prompt.myClones
                : [];
              return {
                ...prompt,
                myClones: [
                  ...currentClones.filter(
                    (clone: any) => clone?.target !== target
                  ),
                  { target, channelId, topicId }
                ]
              };
            })
          };
          return result;
        },
        {} as Record<PromptStudioTab, PromptStudioListState>
      );
      return {
        ...state,
        buildStudio: {
          ...buildStudio,
          promptStudio: nextPromptStudio
        }
      };
    }
    case 'PUBLISH_BUILD_RUNTIME_VERIFY_RESULT': {
      const resultKey = getBuildRuntimeVerifyResultKey(
        action.runtimeVerifyResult
      );
      const requestId = String(
        action.runtimeVerifyResult?.requestId || ''
      ).trim();
      const status =
        action.runtimeVerifyResult?.status === 'error' ? 'error' : 'complete';
      if (!resultKey || !requestId) return state;
      const buildId = Number(action.runtimeVerifyResult?.buildId || 0);
      return {
        ...state,
        runtimeVerifyResults: {
          ...state.runtimeVerifyResults,
          [resultKey]: {
            buildId: buildId > 0 ? buildId : null,
            requestId,
            status,
            improved: action.runtimeVerifyResult?.improved === true,
            reason:
              typeof action.runtimeVerifyResult?.reason === 'string' &&
              action.runtimeVerifyResult.reason.trim().length > 0
                ? action.runtimeVerifyResult.reason
                : null,
            shouldRepairAgain:
              action.runtimeVerifyResult?.shouldRepairAgain === true,
            nextRemainingRepairs: Number.isFinite(
              Number(action.runtimeVerifyResult?.nextRemainingRepairs)
            )
              ? Math.max(
                  0,
                  Math.floor(
                    Number(action.runtimeVerifyResult?.nextRemainingRepairs)
                  )
                )
              : 0,
            error:
              typeof action.runtimeVerifyResult?.error === 'string' &&
              action.runtimeVerifyResult.error.trim().length > 0
                ? action.runtimeVerifyResult.error
                : null
          }
        }
      };
    }
    case 'CLEAR_BUILD_RUNTIME_VERIFY_RESULT': {
      const resultKey = getBuildRuntimeVerifyResultKey(
        action.runtimeVerifyResult
      );
      if (!resultKey || !state.runtimeVerifyResults[resultKey]) return state;
      const nextRuntimeVerifyResults = { ...state.runtimeVerifyResults };
      delete nextRuntimeVerifyResults[resultKey];
      return {
        ...state,
        runtimeVerifyResults: nextRuntimeVerifyResults
      };
    }
    case 'CLEAR_BUILD_RUN': {
      const buildId = Number(action.buildRun?.buildId || 0);
      if (!buildId) return state;
      const key = getBuildRunKey(buildId);
      const currentRun = state.buildRuns[key];
      if (!currentRun) return state;
      const nextRuns = { ...state.buildRuns };
      delete nextRuns[key];
      return {
        ...state,
        buildRuns: nextRuns,
        buildRunRequestMap: removeBuildRunRequestMapping(
          state.buildRunRequestMap,
          currentRun.requestId
        )
      };
    }
    case 'RESET_BUILD_RUNS':
      return {
        ...state,
        buildRuns: {},
        buildRunRequestMap: {},
        buildWorkspaces: {},
        runtimeVerifyResults: {}
      };
    default:
      return state;
  }
}
